import React, { useState, useEffect } from 'react';
import { reservationsApi, guestsApi, paymentsApi, billSettingsApi, invoicesApi, shiftsApi, companyDuesApi } from '../../services/api';
import { calcBill, fmt, fmtDate, genId, PAYMENT_TYPES, toTitleCase, numToWords, getCurrentTimeStr, getTodayStr, getMinBackdateStr, BACKDATE_DAYS, timeTo24h, timeFrom24h, calcLateCheckout } from '../../utils/billing';
import { useAuth } from '../../context/AuthContext';
import { openWhatsAppInvoiceShare } from '../../utils/whatsapp';
import { useNotify } from '../../context/ConfirmContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function getTextValue(value, preferredKeys = []) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(item => getTextValue(item, preferredKeys)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    for (const key of preferredKeys) {
      const nested = getTextValue(value[key], preferredKeys);
      if (nested) return nested;
    }
    return '';
  }
  return String(value).trim();
}

function getHotelNameText(value) {
  return getTextValue(value, ['displayName', 'name', 'hotelName', 'gstName']);
}

function getHotelAddressText(value) {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const parts = [
      getTextValue(value.address),
      getTextValue(value.city),
    ].filter(Boolean);
    return parts.join(', ');
  }
  return getTextValue(value, ['address', 'city']);
}

function getHotelGstText(value) {
  return getTextValue(value, ['gst', 'hotelGst', 'gstNumber', 'gst_number', 'gstName']);
}

function getHotelPhoneText(value) {
  return getTextValue(value, ['phone', 'mobile', 'contact']);
}

export default function CheckOutPage({ hotel, onSuccess, initialResId }) {
  const notify = useNotify();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [activeRes, setActiveRes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [guest, setGuest] = useState(null);
  const [payments, setPayments] = useState([]);
  // Split payment rows for checkout settlement
  const mkPayRow = () => ({ id: Date.now() + Math.random(), type: 'cash', last5: '', amount: '', note: '' });
  const [splitPayments, setSplitPayments] = useState([mkPayRow()]);
  // GST company "pay later": the part of the bill not collected now.
  const emptyPayLater = { enabled: false, contactName: '', contactPhone: '', promisedNote: '' };
  const [payLater, setPayLater] = useState(emptyPayLater);
  const [extraAtCheckout, setExtraAtCheckout] = useState('0');
  const [damageCharges, setDamageCharges] = useState('0');
  const [damageNote, setDamageNote] = useState('');
  const [discountAmount, setDiscountAmount] = useState('0');
  // When the guest actually left. A checkout missed at the desk can be entered
  // up to BACKDATE_DAYS later with the real departure date/time, so nights,
  // the late fee and the saved checkout time match the actual stay.
  const [departureTime, setDepartureTime] = useState('');
  const [departureTimeTouched, setDepartureTimeTouched] = useState(false);

  const [loading, setLoading] = useState(false);
  const [checklist, setChecklist] = useState({
    keys: false,
    damage: false,
    payment: false,
    billReviewed: false,
    others: false,
  });
  const [showGuestEdit, setShowGuestEdit] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const [previewTab, setPreviewTab] = useState('stay');
  const [billDesign, setBillDesign] = useState(null);
  const [invNumPreview, setInvNumPreview] = useState('');
  const [billingOverride, setBillingOverride] = useState(null);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [advanceMode, setAdvanceMode] = useState('full'); // 'full', 'half', 'zero', 'custom'
  const [customAdvance, setCustomAdvance] = useState('0');
  const [shiftActive, setShiftActive] = useState(null); // null=loading, false=no shift, object=active
  const [refund, setRefund] = useState({ type: 'cash', last5: '', amount: '', note: 'Advance refund' });
  const [splitDamageBill, setSplitDamageBill] = useState(false);
  const [splitExtrasBill, setSplitExtrasBill] = useState(false);
  const [savingGuestDetails, setSavingGuestDetails] = useState(false);

  useEffect(() => {
    if (hotel && user) {
      shiftsApi.list(hotel.id)
        .then(allShifts => {
          const mine = allShifts.find(s => s.status === 'active' && String(s.user_id) === String(user.id));
          setShiftActive(mine || false);
        })
        .catch(() => setShiftActive(false));
    }
  }, [hotel, user]);

  useEffect(() => {
    loadActive();
    if (hotel) {
      billSettingsApi.get(hotel.id).then(data => {
        if (data) {
          // Normalize snake_case to camelCase if needed, but keeping both for safety
          setBillDesign({
            ...data,
            hotelName: getHotelNameText(data.hotel_name ?? data.hotelName),
            hotelAddress: getHotelAddressText(data.hotel_address ?? data.hotelAddress),
            hotelGst: getHotelGstText(data.hotel_gst ?? data.hotelGst),
            logoUrl: getTextValue(data.logo_url ?? data.logoUrl),
            footerText: getTextValue(data.footer_text ?? data.footerText),
            termsAndConditions: getTextValue(data.terms_and_conditions ?? data.termsAndConditions),
            signatureLabel: getTextValue(data.signature_label ?? data.signatureLabel)
          });
        }
      }).catch(err => {
        // Non-fatal — generatePDF() falls back to hotel-record fields when billDesign
        // is unset, so checkout can still proceed with default (unbranded) bill styling.
        console.error('Bill design fetch error', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel]);

  async function loadActive() {
    if (!hotel) return;
    try {
      const res = await reservationsApi.list(hotel.id, 'checked-in');
      // Already has guest_name and guest_phone from join
      const mapped = res.map(r => ({
        ...r,
        guestName: r.guest_name,
        guestPhone: r.guest_phone,
        checkIn: r.check_in,
        checkOut: r.check_out,
        checkInTime: r.check_in_time,
        checkOutTime: r.check_out_time,
        totalAmount: Number(r.total_amount),
        roomRate: Number(r.room_rate),
        extraCharges: Number(r.extra_charges),
        billingType: r.billing_type,
        customerType: r.customer_type
      }));
      mapped.sort((a, b) => (a.room || '').localeCompare(b.room || '', undefined, { numeric: true, sensitivity: 'base' }));
      setActiveRes(mapped);
      if (initialResId) {
        const found = mapped.find(r => r.id === initialResId);
        if (found) selectRes(found);
      }
    } catch (err) { console.error('Load Error:', err); }
  }

  async function selectRes(r) {
    try {
      // Fetch latest fields from backend to avoid stale edits discrepancy 
      const [g, pays, latestRes] = await Promise.all([
        guestsApi.list(r.guest_phone),
        paymentsApi.forReservation(r.id),
        reservationsApi.get ? reservationsApi.get(r.id) : Promise.resolve(r) // fallback if not exist
      ]);

      // The reservation's stored `nights` can drift out of sync with its actual
      // check_in/check_out dates — e.g. a stay extended before the fix that made
      // Extend Stay recalculate nights alongside the new checkout date. Rather
      // than trust the stored value (which silently under-bills the extra
      // nights), recompute it from the dates every time checkout loads — this
      // mirrors the same day-diff formula the manual check-out date editor
      // below already uses, so it's a no-op whenever nights was already correct.
      const correctedNights = latestRes?.check_in && latestRes?.check_out
        ? Math.max(1, Math.round((new Date(latestRes.check_out) - new Date(latestRes.check_in)) / 86400000))
        : Number(latestRes?.nights) || 1;

      const updatedRow = latestRes && latestRes.id ? {
        ...latestRes,
        guestName: latestRes.guest_name,
        guestPhone: latestRes.guest_phone,
        checkIn: latestRes.check_in,
        checkOut: latestRes.check_out,
        checkInTime: latestRes.check_in_time,
        checkOutTime: latestRes.check_out_time,
        nights: correctedNights,
        totalAmount: Number(latestRes.total_amount),
        roomRate: Number(latestRes.room_rate),
        extraCharges: Number(latestRes.extra_charges),
        billingType: latestRes.billing_type,
        customerType: latestRes.customer_type,
        company: latestRes.company,
        companyGst: latestRes.company_gst
      } : r;

      setSelected({ ...updatedRow, originalCheckOut: String(updatedRow.checkOut || '').split('T')[0] });
      setSelectedRooms((updatedRow.room || '').split(',').map(s => s.trim()));
      // Overdue stay (planned checkout already passed): assume the guest left at
      // the standard 12:00 on that day unless staff enter another time.
      setDepartureTime(String(updatedRow.checkOut || '').split('T')[0] < getTodayStr() ? '12:00' : timeTo24h(getCurrentTimeStr()));
      setDepartureTimeTouched(false);
      setAdvanceMode('full'); // Reset advance mode on selection
      setCustomAdvance('0');
      setBillingOverride(null); // Reset override on new selection
      setShowGuestEdit(false);
      setRefund({ type: 'cash', last5: '', amount: '', note: 'Advance refund' });
      setSplitPayments([mkPayRow()]);
      setPayLater(emptyPayLater);
      setSplitDamageBill(false);

      const guestObj = g.length > 0 ? {
        ...g[0],
        companyGst: g[0].company_gst,
        customerType: g[0].customer_type,
        idType: g[0].id_type,
        idNumber: g[0].id_number
      } : { name: updatedRow.guestName, phone: updatedRow.guestPhone, id: updatedRow.guest_id };
      setGuest(guestObj);
      setPayments(pays);

      setExtraAtCheckout('0');
      setDamageCharges('0');
      setDamageNote('');
      setDiscountAmount('0');
    } catch (err) { console.error('Select Error:', err); }
  }


  const filtered = activeRes.filter(r =>
    !search || r.guestName?.toLowerCase().includes(search.toLowerCase()) ||
    r.guestPhone?.includes(search) || r.room?.includes(search)
  );

  const paidSoFarTotal = payments.reduce((a, p) => a + Number(p.amount), 0);

  const allRoomsList = (selected?.room || '').split(',').map(s => s.trim()).filter(Boolean);
  const isPartial = selected && selectedRooms.length > 0 && selectedRooms.length < allRoomsList.length;

  const getAttributedAdvance = () => {
    if (!isPartial) return paidSoFarTotal;
    if (advanceMode === 'full') return paidSoFarTotal;
    if (advanceMode === 'half') return paidSoFarTotal / 2;
    if (advanceMode === 'zero') return 0;
    return Number(customAdvance) || 0;
  };

  const paidSoFar = getAttributedAdvance();
  const totalExtra = (Number(selected?.extraCharges) || 0) + Number(extraAtCheckout || 0) + Number(damageCharges || 0);
  const stayOnlyExtra = (Number(selected?.extraCharges) || 0) + Number(extraAtCheckout || 0);
  const damageOnlyExtra = Number(damageCharges || 0);

  const computeLateCheckoutInfo = () => calcLateCheckout({
    checkOutDate: selected?.checkOut || selected?.check_out,
    departureTime,
    now: new Date(),
    graceMins: Number(hotel?.late_checkout_grace_mins ?? hotel?.lateCheckoutGraceMins ?? 30),
    hourlyRate: Number(hotel?.late_checkout_hourly_rate ?? hotel?.lateCheckoutHourlyRate ?? 200),
    halfDayHours: Number(hotel?.late_checkout_half_day_hours ?? hotel?.lateCheckoutHalfDayHours ?? 4),
    roomRate: Number(currentRoomRate || selected?.roomRate || selected?.room_rate || 0),
  });

  const currentBillingType = billingOverride || selected?.billingType || selected?.billing_type || (selected?.customerType === 'corporate' ? 'gst' : 'non-gst');
  const currentBillingCycle = selected?.billingCycle || selected?.billing_cycle || '24h';

  const getParsedRoomConfigs = (reservation) => {
    if (!reservation?.room_configs && !reservation?.roomConfigs) return [];
    try {
      const raw = reservation.room_configs ?? reservation.roomConfigs;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Room config parse error', e);
      return [];
    }
  };

  const getSelectedPricingMeta = (reservation, selectedRoomNumbers) => {
    const configs = getParsedRoomConfigs(reservation);
    const relevantConfigs = configs.filter(config => {
      const roomsInConfig = (config.room || '').split(',').map(s => s.trim()).filter(Boolean);
      return roomsInConfig.some(roomNumber => selectedRoomNumbers.includes(roomNumber));
    });

    const hasSpecialRate = relevantConfigs.some(config => {
      const basePrice = Number(config.basePrice ?? config.base_price ?? 0);
      const offeredPrice = Number(config.offeredPrice ?? config.offered_price ?? config.roomRate ?? config.room_rate ?? 0);
      return basePrice > 0 && offeredPrice > 0 && basePrice !== offeredPrice;
    });

    if (!hasSpecialRate) {
      return {
        hasSpecialRate: false,
        note: ''
      };
    }

    if (relevantConfigs.length === 1) {
      const config = relevantConfigs[0];
      const basePrice = Number(config.basePrice ?? config.base_price ?? 0);
      const offeredPrice = Number(config.offeredPrice ?? config.offered_price ?? config.roomRate ?? config.room_rate ?? 0);
      return {
        hasSpecialRate: true,
        note: `Special rate applied (${fmt(offeredPrice)}/room vs ${fmt(basePrice)} standard)`
      };
    }

    return {
      hasSpecialRate: true,
      note: 'Special rate applied on selected rooms'
    };
  };

  const stayUnitLabel = currentBillingCycle === '12h' ? 'Slot(s)' : 'Night(s)';
  const stayPricingMeta = selected ? getSelectedPricingMeta(selected, selectedRooms) : { hasSpecialRate: false, note: '' };

  // Calculate Rate for Selected Rooms ONLY
  const getSelectedRate = () => {
    if (!selected) return 0;
    const allR = (selected.room || '').split(',').map(s => s.trim()).filter(Boolean);
    const storedRate = Number(selected.roomRate);

    // PRIMARY SOURCE: Master 'room_rate' accurately updated during Check-in/Splits
    if (storedRate > 0) {
      if (selectedRooms.length === allR.length) return storedRate;
      // Proportional split fallback if partially checking out from the master string
      const perRoom = storedRate / Math.max(allR.length, 1);
      return perRoom * selectedRooms.length;
    }

    // FALLBACK: Parse room configs (Used to fix legacy zero-rate bugs)
    try {
      const configs = selected.room_configs ? JSON.parse(selected.room_configs) : [];
      if (configs.length > 0 && configs.some(c => Number(c.roomRate) > 0)) {
        // Check if all selected rooms are in configs
        let totalFromConfigs = 0;
        let coveredRooms = 0;
        configs.forEach(c => {
          const roomsInConfig = (c.room || '').split(',').map(s => s.trim()).filter(Boolean);
          const selectedInConfig = roomsInConfig.filter(r => selectedRooms.includes(r));
          if (selectedInConfig.length === 0) return;
          const configRate = Number(c.roomRate) || 0;
          if (configRate > 0 && roomsInConfig.length > 0) {
            const perRoom = configRate / roomsInConfig.length;
            totalFromConfigs += perRoom * selectedInConfig.length;
            coveredRooms += selectedInConfig.length;
          }
        });
        if (coveredRooms > 0 && totalFromConfigs > 0) return totalFromConfigs;
      }
    } catch (e) { console.error('Config parse error', e); }

    return 0;
  };

  const currentRoomRate = getSelectedRate();

  const getSelectedRoomTypes = () => {
    if (!selected) return '';
    try {
      const configs = selected.room_configs ? JSON.parse(selected.room_configs) : [];
      if (configs.length > 0) {
        const typesSeen = new Set();
        selectedRooms.forEach(rn => {
          const cfg = configs.find(c => (c.room || '').split(',').map(s => s.trim()).includes(rn));
          if (cfg) typesSeen.add(cfg.roomType);
        });
        if (typesSeen.size > 0) return Array.from(typesSeen).join(' + ');
      }
      // Fallback: if only a subset of rooms selected, find the type by room number position
      const allRooms = (selected.room || '').split(',').map(s => s.trim());
      const allTypes = (selected.roomType || selected.room_type || '').split('+').map(s => s.trim());
      if (allRooms.length > 1 && allTypes.length > 1) {
        const typesSeen = new Set();
        selectedRooms.forEach(rn => {
          const idx = allRooms.indexOf(rn);
          if (idx >= 0 && allTypes[idx]) typesSeen.add(allTypes[idx]);
        });
        if (typesSeen.size > 0) return Array.from(typesSeen).join(' + ');
      }
    } catch (e) { console.error('Type parse error', e); }
    // If only one room selected from many, show full type as last resort
    return selected.roomType || selected.room_type || '';
  };
  const activeRoomTypes = getSelectedRoomTypes();

  const bill = selected ? calcBill(
    { ...selected, roomRate: currentRoomRate, extraCharges: totalExtra, advancePaid: paidSoFar, discount: discountAmount },
    currentBillingType,
    hotel?.gstRate
  ) : null;

  const stayChargeTarget = (splitExtrasBill ? 0 : stayOnlyExtra) + (splitDamageBill ? 0 : damageOnlyExtra);
  const stayBill = selected ? calcBill(
    { ...selected, roomRate: currentRoomRate, extraCharges: stayChargeTarget, advancePaid: paidSoFar, discount: discountAmount },
    currentBillingType,
    hotel?.gstRate
  ) : null;

  const extrasBill = selected ? calcBill(
    { ...selected, roomRate: 0, nights: 1, extraCharges: stayOnlyExtra, advancePaid: 0, discount: 0 },
    currentBillingType,
    hotel?.gstRate
  ) : null;

  const damageBill = selected ? calcBill(
    { ...selected, roomRate: 0, nights: 1, extraCharges: damageOnlyExtra, advancePaid: 0, discount: 0 },
    currentBillingType,
    hotel?.gstRate
  ) : null;

  // React to the balanceDue value only; `bill` is a freshly computed object
  // every render (not memoized), so depending on it directly would re-run this
  // on every unrelated render.
  useEffect(() => {
    if (bill) {
      setRefund(r => ({ ...r, amount: String(Math.max(0, -(bill.balanceDue || 0))) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill?.balanceDue]);

  const currentTotal = bill?.total || 0;
  const finalBalance = currentTotal - paidSoFar;
  const refundDue = Math.max(0, paidSoFar - currentTotal);

  const resetBillReviewIfNeeded = () => {
    setChecklist(prev => (prev.billReviewed ? { ...prev, billReviewed: false } : prev));
  };

  const handleGuestFieldChange = (field, value, syncReservation = false) => {
    let normalizedValue = value;
    if (field === 'companyGst') {
      normalizedValue = String(value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 15);
    }
    resetBillReviewIfNeeded();
    setGuest(prev => (prev ? { ...prev, [field]: normalizedValue } : { [field]: normalizedValue }));
    if (syncReservation) {
      setSelected(prev => (prev ? { ...prev, [field]: normalizedValue } : prev));
    }
  };

  const buildGuestPayload = (guestData = guest, reservationData = selected) => {
    const customerType = guestData?.customerType || reservationData?.customerType || (reservationData?.company || guestData?.company ? 'corporate' : 'individual');
    return {
      hotelId: hotel?.id,
      salutation: String(guestData?.salutation || '').trim(),
      name: String(guestData?.name || '').trim(),
      gender: guestData?.gender || '',
      phone: String(guestData?.phone || '').trim(),
      email: String(guestData?.email || '').trim(),
      nationality: guestData?.nationality || '',
      dob: guestData?.dob || null,
      customerType,
      idType: guestData?.idType || '',
      idNumber: guestData?.idNumber || '',
      address: String(guestData?.address || '').trim(),
      company: String(reservationData?.company || guestData?.company || '').trim(),
      companyGst: String(reservationData?.companyGst || guestData?.companyGst || '').trim().replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 15)
    };
  };

  const handleSaveGuestDetails = async () => {
    if (!selected || !guest) return;

    const guestId = guest.id || selected.guestId || selected.guest_id;
    if (!guestId) {
      onSuccess?.('Guest record not found for this stay.', 'error');
      return;
    }

    const guestPayload = buildGuestPayload(guest, selected);
    if (!guestPayload.name || !guestPayload.phone) {
      onSuccess?.('Guest name and phone are required before saving.', 'error');
      return;
    }

    if (guestPayload.companyGst && guestPayload.companyGst.length !== 15) {
      onSuccess?.('Company GSTIN must be exactly 15 characters (e.g. 37AAAAA0000A1Z5).', 'error');
      return;
    }

    setSavingGuestDetails(true);
    try {
      await guestsApi.update(guestId, guestPayload);

      const reservationPatch = {
        customerType: guestPayload.customerType,
        company: guestPayload.company,
        companyGst: guestPayload.companyGst,
        billingType: currentBillingType
      };
      await reservationsApi.update(selected.id, reservationPatch);

      setGuest(prev => prev ? { ...prev, ...guestPayload, id: guestId } : { ...guestPayload, id: guestId });
      setSelected(prev => prev ? {
        ...prev,
        ...reservationPatch,
        guestId,
        guest_id: guestId
      } : prev);

      onSuccess?.('Guest details saved. You can print the updated bill now.', 'success');
    } catch (err) {
      onSuccess?.(`Could not save guest details: ${err.message}`, 'error');
    } finally {
      setSavingGuestDetails(false);
    }
  };

  const formatDisplayName = (salutation, name) => {
    const cleanSalutation = toTitleCase(String(salutation || '').trim());
    const cleanName = toTitleCase(String(name || '').trim());
    return [cleanSalutation, cleanName].filter(Boolean).join(' ') || 'Guest';
  };

  const formatPaymentTypeLabel = (type) => {
    const raw = String(type || '').replace(/_/g, ' ').trim();
    return raw ? raw.replace(/\b\w/g, c => c.toUpperCase()) : 'Payment';
  };

  const formatCurrencyCell = (amount) => {
    const numeric = Number(amount) || 0;
    return numeric < 0 ? `- ${fmt(Math.abs(numeric))}` : fmt(numeric);
  };

  // ─── SETTLEMENT (what is actually collected at checkout) ────────────────
  // The bill's "Settlement Details" and the payments saved must both come from
  // the split rows staff filled in (cash / UPI / card / half-half), plus any
  // amount a GST company will pay later. Together they must equal the balance.
  const NON_CASH_TYPES = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'];
  const roundMoney = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const splitTotal = roundMoney(splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0));
  const companyPayload = selected ? buildGuestPayload(guest, selected) : null;
  // A GST bill with a company name and a full GSTIN is a company bill — the
  // guest's Individual/Corporate flag is often left unchanged, so it isn't required.
  // TODO(company-dues): switch on once the server's /company-dues routes are
  // deployed — until then a Pay Later checkout would fail after the stay is
  // marked checked-out and leave it without an invoice.
  const COMPANY_DUES_ENABLED = false;
  const payLaterEligible = COMPANY_DUES_ENABLED && Boolean(selected) && currentBillingType === 'gst'
    && Boolean(companyPayload?.company) && companyPayload?.companyGst?.length === 15;
  const payLaterActive = payLaterEligible && payLater.enabled && refundDue <= 0;
  const payLaterAmount = payLaterActive ? Math.max(0, roundMoney(finalBalance - splitTotal)) : 0;
  const payLaterLabel = `Due from ${companyPayload?.company || 'company'} (pay later)`;

  const settlementRecord = refundDue > 0
    ? []
    : [
      ...splitPayments
        .filter(sp => Number(sp.amount) > 0)
        .map(sp => ({ type: sp.type, amount: Number(sp.amount), note: sp.note?.trim() || (sp.last5 ? `Ref ${sp.last5}` : 'Final settlement') })),
      ...(payLaterAmount > 0 ? [{ type: 'pay_later', amount: payLaterAmount, label: payLaterLabel, isDue: true }] : []),
    ];

  // Returns an error message, or null when checkout may proceed.
  const validateSettlement = () => {
    if (!selected) return 'Select a guest first.';
    if (refundDue > 0) {
      const refundAmt = Number(refund.amount) || 0;
      if (Math.abs(refundAmt - refundDue) > 0.5) return `Refund entered (${fmt(refundAmt)}) must match the refund due (${fmt(refundDue)}).`;
      if (NON_CASH_TYPES.includes(refund.type) && !String(refund.last5 || '').trim()) return 'Reference number (last 5 digits) is required for a non-cash refund.';
      return null;
    }
    if (splitPayments.some(sp => Number(sp.amount) > 0 && NON_CASH_TYPES.includes(sp.type) && !String(sp.last5 || '').trim())) {
      return 'Reference number (last 5 digits) is required for every non-cash payment.';
    }
    if (finalBalance <= 0.5) return null;
    if (payLaterActive) {
      if (payLaterAmount <= 0) return 'Pay Later is on, but the payments already cover the full bill — switch Pay Later off.';
      if (!payLater.contactName.trim()) return 'Enter the company contact person for Pay Later.';
      if (payLater.contactPhone.replace(/\D/g, '').length !== 10) return 'Enter a 10-digit contact phone for Pay Later.';
      return null;
    }
    const diff = roundMoney(finalBalance - splitTotal);
    if (Math.abs(diff) > 0.5) {
      return diff > 0
        ? `Payments entered (${fmt(splitTotal)}) don't match the bill balance (${fmt(finalBalance)}) — ${fmt(diff)} is still unpaid.${payLaterEligible ? ' Collect it now or use Company Pay Later.' : ''}`
        : `Payments entered (${fmt(splitTotal)}) are more than the bill balance (${fmt(finalBalance)}).`;
    }
    return null;
  };

  const fillFullPayment = (type) => {
    setSplitPayments([{ ...mkPayRow(), type, amount: String(roundMoney(Math.max(0, finalBalance))) }]);
    setPayLater(pl => ({ ...pl, enabled: false }));
  };

  const getInvoiceLineItems = (reservation, billData, billingType, options = {}) => {
    const {
      includeDamage = true,
      roomAmountOverride = null,
      excludeRoomStay = false,
      excludeExtras = false,
      titleSuffix = ''
    } = options;
    const rows = [];
    
    if (!excludeRoomStay) {
      rows.push({
        description: `Room Stay Charges${stayPricingMeta.hasSpecialRate ? ' - Special Rate' : ''} (${selectedRooms.length} Room${selectedRooms.length > 1 ? 's' : ''}: ${selectedRooms.join(', ')})${titleSuffix ? ` ${titleSuffix}` : ''}`,
        code: '996311',
        details: `${reservation.nights} ${currentBillingCycle === '12h' ? 'slot' : 'night'}${reservation.nights > 1 ? 's' : ''}`,
        amount: roomAmountOverride ?? (currentRoomRate * reservation.nights)
      });
    }

    // Items paid for on the spot (settlement:'paid') were already collected and
    // aren't part of the room bill — extra_charges on the server excludes them too,
    // so they're left off this itemized invoice entirely to avoid double-counting.
    const allSvcs = typeof reservation.extra_services === 'string' ? JSON.parse(reservation.extra_services || '[]') : (reservation.extra_services || []);
    const svcs = allSvcs.filter(s => s.settlement !== 'paid');
    const svcTotal = svcs.reduce((a, s) => a + Number(s.amount), 0);
    const extraChargesVal = Number(reservation.extra_charges || reservation.extraCharges || 0);
    const extraAtCOVal = Number(extraAtCheckout || 0);
    
    // If excludeExtras is true or extraAmountOverride is 0, skip stay extras for room bill.
    const skipSvcs = excludeExtras || options.extraAmountOverride === 0;
    
    if (svcs.length > 0 && !skipSvcs) {
        svcs.forEach(s => {
             const d = new Date(s.date);
             const dateStr = !isNaN(d) ? `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : '';
             const timeSuffix = dateStr ? ` [${dateStr}]` : '';
             const qtyStr = s.quantity > 1 ? ` (${s.quantity} × ${s.unitPrice})` : (s.unitPrice ? ` (1 × ${s.unitPrice})` : '');
             
             rows.push({
                description: `${s.name}${timeSuffix}${qtyStr}`,
                code: '996331',
                details: s.quantity && s.unitPrice ? `${s.quantity} qty` : '-',
                amount: Number(s.amount)
             });
        });
    }

    const defaultExtra = extraChargesVal - svcTotal + extraAtCOVal;
    let remainingExtra = defaultExtra;
    
    if (skipSvcs) {
        remainingExtra = 0; // Skip entirely when split billing / excludeExtras is active
    } else if (options.extraAmountOverride !== undefined && options.extraAmountOverride !== null) {
        remainingExtra = Math.max(0, options.extraAmountOverride - svcTotal);
    }

    if (remainingExtra > 0) {
        rows.push({
            description: 'Service Charges / Extras',
            code: '996331',
            details: '-',
            amount: remainingExtra
        });
    }

    if (includeDamage && Number(damageCharges) > 0) {
      rows.push({
        description: `Damage Charges${damageNote ? `: ${damageNote}` : ''}`,
        code: '-',
        details: '-',
        amount: Number(damageCharges),
        tone: 'danger'
      });
    }

    if (Number(discountAmount) > 0) {
      rows.push({
        description: 'Discount Applied',
        code: '-',
        details: '-',
        amount: -Number(discountAmount),
        tone: 'success'
      });
    }

    if (billingType === 'gst' && Number(billData?.gstAmount) > 0) {
      rows.push({
        description: `CGST (${billData.gstRate / 2}%)`,
        code: '-',
        details: '-',
        amount: billData.gstAmount / 2
      });
      rows.push({
        description: `SGST (${billData.gstRate / 2}%)`,
        code: '-',
        details: '-',
        amount: billData.gstAmount / 2
      });
    }

    return rows;
  };

  // The printed bill always shows the company's pay-later part simply as "Paid"
  // (fully settled from the guest's side) — the hotel tracks the actual
  // collection internally under Cash Management → Company Dues.
  const toSettlementEntry = entry => (entry.isDue
    ? { label: 'Paid', amount: Number(entry.amount) || 0, isDue: false }
    : {
      label: entry.label || `${formatPaymentTypeLabel(entry.type)}${entry.note ? ` (${entry.note})` : ''}`,
      amount: Number(entry.amount) || 0,
      isDue: false
    });

  const getSettlementEntries = (paymentsList, finalPayments) =>
    [...paymentsList, ...[].concat(finalPayments || [])]
      .filter(entry => Number(entry?.amount) > 0)
      .map(toSettlementEntry);

  const paidOnly = entries => entries.reduce((sum, e) => sum + e.amount, 0);

  const splitSettlementEntries3Way = (paymentsList, finalPayment, stayT, extrasT, damageT) => {
    const sourceEntries = [...paymentsList, ...[].concat(finalPayment || [])]
      .filter(entry => Number(entry?.amount) > 0)
      .map(toSettlementEntry);

    let remStay = Math.max(0, Number(stayT) || 0);
    let remExtras = Math.max(0, Number(extrasT) || 0);
    let remDamage = Math.max(0, Number(damageT) || 0);

    const stayEntries = [];
    const extrasEntries = [];
    const damageEntries = [];

    sourceEntries.forEach(entry => {
      let left = entry.amount;
      if (remStay > 0) {
        const used = Math.min(left, remStay);
        if (used > 0) {
          stayEntries.push({ label: entry.label, amount: used, isDue: entry.isDue });
          remStay -= used;
          left -= used;
        }
      }
      if (left > 0 && remExtras > 0) {
        const used = Math.min(left, remExtras);
        if (used > 0) {
          extrasEntries.push({ label: entry.label, amount: used, isDue: entry.isDue });
          remExtras -= used;
          left -= used;
        }
      }
      if (left > 0 && remDamage > 0) {
        const used = Math.min(left, remDamage);
        if (used > 0) {
          damageEntries.push({ label: entry.label, amount: used, isDue: entry.isDue });
          remDamage -= used;
          left -= used;
        }
      }
    });

    return { stayEntries, extrasEntries, damageEntries };
  };

  const generatePDF = async (reservation, guestData, hotelData, billData, finalPayment, paymentsList, options = {}) => {
    try {
      const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 12;
      let y = margin;
      const invNum = options.invoiceNumber || invNumPreview || genId('INV');
      const hName = getHotelNameText(billDesign?.hotelName) || getHotelNameText(hotelData) || 'Hotel';
      const hAddr = getHotelAddressText(billDesign?.hotelAddress) || getHotelAddressText(hotelData) || 'NA';
      const hGst = getHotelGstText(billDesign?.hotelGst) || getHotelGstText(hotelData) || 'NA';
      const hPhone = getHotelPhoneText(hotelData) || 'NA';
      const hColor = hotelData.color || '#1f3a5f';
      const logoUrl = billDesign?.logoUrl || billDesign?.logo_url;
      const isGst = currentBillingType === 'gst';
      const issueAt = new Date();
      const issueDate = issueAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const issueTime = issueAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
      const guestName = formatDisplayName(guestData.salutation, guestData.name);
      const guestAddressRaw = getTextValue(guestData.address);
      const guestAddress = guestAddressRaw ? toTitleCase(guestAddressRaw) : 'NA';
      const companyName = getTextValue(reservation.company) || getTextValue(guestData.company) || 'NA';
      const companyGst = getTextValue(reservation.companyGst) || getTextValue(guestData.companyGst) || 'NA';
      const invoiceItems = options.invoiceItems || getInvoiceLineItems(reservation, billData, currentBillingType);
      const settlementEntries = options.settlementEntries || getSettlementEntries(paymentsList, finalPayment);
      const totalPaid = paidOnly(settlementEntries);
      const taxableBase = Number(options.taxableBase ?? billData?.subtotal ?? (billData?.total || 0) - (billData?.gstAmount || 0));
      const pdfTotal = Number(options.totalAmount ?? billData?.total ?? 0);
      const invoiceKindLabel = options.invoiceKindLabel || (isGst ? 'GST Invoice' : 'Invoice');
      const hideInvoiceNumber = Boolean(options.hideInvoiceNumber);
      const hideHotelTaxNumber = Boolean(options.hideHotelTaxNumber);
      const displayDocumentLabel = options.documentLabel || invoiceKindLabel;

      const hexToRgb = (hex) => {
        const normalized = /^#?[0-9A-Fa-f]{6}$/.test(String(hex || '').trim())
          ? String(hex).replace('#', '')
          : '1f3a5f';
        return [
          parseInt(normalized.slice(0, 2), 16),
          parseInt(normalized.slice(2, 4), 16),
          parseInt(normalized.slice(4, 6), 16)
        ];
      };
      const [accentR, accentG, accentB] = hexToRgb(hColor);
      const darkText = [33, 43, 57];
      const mutedText = [96, 106, 119];
      const softFill = [246, 248, 251];
      const borderColor = [216, 222, 230];
      const dangerText = [172, 57, 57];
      const successText = [33, 124, 81];

      const addContinuationHeader = () => {
        doc.setFillColor(accentR, accentG, accentB);
        doc.rect(0, 0, pageWidth, 5, 'F');
        y = 16;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11.5);
        doc.setTextColor(...darkText);
        doc.text(hName, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...mutedText);
        if (!hideInvoiceNumber) {
          doc.text(`Invoice ${invNum}`, pageWidth - margin, y, { align: 'right' });
        }
        doc.setDrawColor(...borderColor);
        doc.line(margin, y + 4, pageWidth - margin, y + 4);
        y += 10;
      };

      const ensureSpace = needed => {
        if (y + needed > pageHeight - 18) {
          doc.addPage();
          addContinuationHeader();
        }
      };

      if (logoUrl) {
        try {
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: 0.035 }));
          doc.addImage(logoUrl, 'PNG', (pageWidth - 115) / 2, (pageHeight - 115) / 2, 115, 115, undefined, 'FAST');
          doc.restoreGraphicsState();
        } catch (e) {
          console.warn('Watermark failed:', e.message);
        }
      }

      doc.setFillColor(accentR, accentG, accentB);
      doc.rect(0, 0, pageWidth, 6, 'F');

      let textStartX = margin;
      const logoWidth = 24;
      const logoHeight = 18;
      if (logoUrl) {
        try {
          doc.addImage(logoUrl, 'PNG', margin, 13, logoWidth, logoHeight, undefined, 'FAST');
          textStartX = margin + logoWidth + 5;
        } catch (e) {
          console.warn('Logo header failed:', e.message);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(...darkText);
      doc.text(hName, textStartX, 19);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...mutedText);
      doc.text(hideHotelTaxNumber ? `Phone: ${hPhone}` : `GSTIN: ${hGst}   Phone: ${hPhone}`, textStartX, 24);
      const hotelAddressLines = doc.splitTextToSize(hAddr, 94);
      doc.text(hotelAddressLines, textStartX, 29);

      const metaBoxX = pageWidth - margin - 61;
      const metaBoxY = 12;
      doc.setFillColor(252, 253, 255);
      doc.setDrawColor(...borderColor);
      doc.roundedRect(metaBoxX, metaBoxY, 61, 31, 2, 2, 'FD');
      doc.setFillColor(accentR, accentG, accentB);
      doc.roundedRect(metaBoxX, metaBoxY, 61, 8, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(255, 255, 255);
      doc.text(displayDocumentLabel.toUpperCase(), metaBoxX + 30.5, metaBoxY + 5.4, { align: 'center' });
      if (!hideInvoiceNumber) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...darkText);
        doc.text(`#${invNum}`, metaBoxX + 56.5, metaBoxY + 15.5, { align: 'right' });
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...mutedText);
      doc.text(`Issued: ${issueDate}`, metaBoxX + 4, hideInvoiceNumber ? metaBoxY + 16.5 : metaBoxY + 21.5);
      doc.text(`Time: ${issueTime}`, metaBoxX + 4, hideInvoiceNumber ? metaBoxY + 21.5 : metaBoxY + 26.5);

      const headerBottom = Math.max(29 + (hotelAddressLines.length * 4.1), metaBoxY + 31) + 5;
      doc.setDrawColor(...borderColor);
      doc.line(margin, headerBottom, pageWidth - margin, headerBottom);
      y = headerBottom + 7;

      const cardGap = 6;
      const cardWidth = (pageWidth - margin * 2 - cardGap) / 2;
      const guestAddressLines = doc.splitTextToSize(guestAddress, cardWidth - 22);
      const guestCardHeight = 24 + (guestAddressLines.length * 4) + (guestData.email ? 5 : 0) + (guestData.customerType === 'corporate' ? 10 : 0);
      const stayCardHeight = 36;
      const cardHeight = Math.max(guestCardHeight, stayCardHeight);

      doc.setFillColor(...softFill);
      doc.setDrawColor(...borderColor);
      doc.roundedRect(margin, y, cardWidth, cardHeight, 2, 2, 'FD');
      doc.roundedRect(margin + cardWidth + cardGap, y, cardWidth, cardHeight, 2, 2, 'FD');

      let iy = y + 7;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...darkText);
      doc.text('Guest Details', margin + 4, iy);
      iy += 6;
      doc.setFontSize(8);
      doc.text('Name', margin + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(guestName, margin + 20, iy);
      iy += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('Address', margin + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(guestAddressLines, margin + 20, iy);
      iy += guestAddressLines.length * 4;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('Phone', margin + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(guestData.phone || 'NA', margin + 20, iy);
      if (guestData.email) {
        iy += 5;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        doc.text('Email', margin + 4, iy);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...mutedText);
        doc.text(guestData.email, margin + 20, iy);
      }
      if (guestData.customerType === 'corporate') {
        iy += 5;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        doc.text('Company', margin + 4, iy);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...mutedText);
        doc.text(companyName, margin + 20, iy);
        iy += 5;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        doc.text('GST No.', margin + 4, iy);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...mutedText);
        doc.text(companyGst, margin + 20, iy);
      }

      const stayX = margin + cardWidth + cardGap;
      iy = y + 7;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...darkText);
      doc.text('Stay Details', stayX + 4, iy);
      iy += 6;
      doc.setFontSize(8);
      doc.text('Room', stayX + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(reservation.room || 'NA', stayX + 22, iy);
      iy += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('Category', stayX + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(reservation.roomType || 'NA', stayX + 22, iy);
      iy += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('Check-in', stayX + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(`${fmtDate(reservation.checkIn || reservation.check_in)} ${reservation.checkInTime || reservation.check_in_time || ''}`.trim(), stayX + 22, iy);
      iy += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('Check-out', stayX + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(fmtDate(reservation.checkOut || reservation.check_out), stayX + 22, iy);
      iy += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('Duration', stayX + 4, iy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedText);
      doc.text(`${reservation.nights} ${currentBillingCycle === '12h' ? 'Slot(s)' : 'Night(s)'}`, stayX + 22, iy);


      y += cardHeight + 8;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Description', 'SAC Code', 'Details', 'Amount']],
        body: invoiceItems.map(item => [
          item.description,
          item.code || '-',
          item.details || '-',
          formatCurrencyCell(item.amount)
        ]),
        foot: [['GRAND TOTAL', '', '', fmt(pdfTotal)]],
        theme: 'grid',
        styles: {
          fontSize: 8.5,
          lineColor: borderColor,
          lineWidth: 0.2,
          textColor: darkText,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [34, 44, 60],
          textColor: 255,
          fontStyle: 'bold',
          cellPadding: 3.2
        },
        footStyles: {
          fillColor: [44, 58, 77],
          textColor: 255,
          fontSize: 9.5,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 88, fontStyle: 'bold' },
          1: { cellWidth: 24, halign: 'center' },
          2: { cellWidth: 28, halign: 'center' },
          3: { cellWidth: 38, halign: 'right', fontStyle: 'bold' }
        },
        alternateRowStyles: { fillColor: softFill },
        didParseCell: data => {
          // columnStyles' halign only reliably applies to body cells in jspdf-autotable —
          // head/foot cells fall back to left alignment regardless, which misaligned the
          // "Amount" header label and the GRAND TOTAL value against the right-aligned
          // amounts below them. Force the same per-column alignment on every section here.
          const columnAlign = { 1: 'center', 2: 'center', 3: 'right' }[data.column.index];
          if (columnAlign) {
            data.cell.styles.halign = columnAlign;
          }
          if (data.section === 'body') {
            const row = invoiceItems[data.row.index];
            if (row?.tone === 'danger') {
              data.cell.styles.textColor = dangerText;
            }
            if (row?.tone === 'success') {
              data.cell.styles.textColor = successText;
            }
          }
        }
      });

      y = doc.lastAutoTable.finalY + 7;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8.3);
      doc.setTextColor(...mutedText);
      doc.text(`Amount in words: ${numToWords(pdfTotal)}`, margin, y);
      y += 7;

      ensureSpace(42);
      const settlementWidth = 102;
      const summaryWidth = pageWidth - margin * 2 - settlementWidth - 6;
      const settlementHeight = Math.max(26, 16 + (Math.max(settlementEntries.length, 1) * 5));
      const summaryHeight = 31;
      const blockHeight = Math.max(settlementHeight, summaryHeight);

      doc.setFillColor(...softFill);
      doc.setDrawColor(...borderColor);
      doc.roundedRect(margin, y, settlementWidth, blockHeight, 2, 2, 'FD');
      doc.roundedRect(margin + settlementWidth + 6, y, summaryWidth, blockHeight, 2, 2, 'FD');

      let sy = y + 6;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...darkText);
      doc.text('Settlement Details', margin + 4, sy);
      sy += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...mutedText);
      if (settlementEntries.length === 0) {
        doc.text('No payment entries recorded.', margin + 4, sy);
      } else {
        settlementEntries.forEach(entry => {
          doc.text(entry.label, margin + 4, sy);
          doc.text(fmt(entry.amount), margin + settlementWidth - 4, sy, { align: 'right' });
          sy += 5;
        });
      }

      sy = y + 6;
      const summaryX = margin + settlementWidth + 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...darkText);
      doc.text('Invoice Summary', summaryX, sy);
      sy += 6;
      const summaryRows = options.summaryRows || [
        ['Taxable Amount', fmt(taxableBase)],
        ['GST Included', fmt(billData?.gstAmount || 0)],
        ['Total Paid', fmt(totalPaid)],
      ];
      summaryRows.forEach(([label, value], index) => {
        const isBalance = index === summaryRows.length - 1;
        doc.setFont('helvetica', isBalance ? 'bold' : 'normal');
        doc.setFontSize(isBalance ? 8.6 : 8.2);
        doc.setTextColor(...(isBalance ? darkText : mutedText));
        doc.text(label, summaryX, sy);
        doc.text(value, margin + settlementWidth + 6 + summaryWidth - 4, sy, { align: 'right' });
        sy += 5.3;
      });

      y += blockHeight + 8;

      const termsText = billDesign?.termsAndConditions || [
        "1. All guests must provide valid ID proof during check-in.",
        "2. Management is not responsible for any loss of valuable belongings.",
        "3. Any damage to hotel property will be charged to the guest.",
        "4. Final bill must be settled in full before check-out."
      ].join('\n');
      const termsLines = doc.splitTextToSize(termsText, pageWidth - margin * 2 - 8);
      const termsHeight = 12 + (termsLines.length * 4.2);
      ensureSpace(termsHeight + 28);

      doc.setFillColor(...softFill);
      doc.setDrawColor(...borderColor);
      doc.roundedRect(margin, y, pageWidth - margin * 2, termsHeight, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...darkText);
      doc.text('Terms & Conditions', margin + 4, y + 6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...mutedText);
      doc.text(termsLines, margin + 4, y + 12);
      y += termsHeight + 12;

      const signatureBlockHeight = 22;
      ensureSpace(signatureBlockHeight + 10);
      const signatureLineY = y + 14;
      doc.setDrawColor(...borderColor);
      doc.line(margin, signatureLineY, margin + 55, signatureLineY);
      doc.line(pageWidth - margin - 55, signatureLineY, pageWidth - margin, signatureLineY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.2);
      doc.setTextColor(...mutedText);
      doc.text('Guest Signature', margin + 27.5, signatureLineY + 4.5, { align: 'center' });
      doc.text(billDesign?.signatureLabel || 'Authorized Signatory / Seal', pageWidth - margin - 27.5, signatureLineY + 4.5, { align: 'center' });
      y += signatureBlockHeight;

      const footerCopy = billDesign?.footerText || 'Thank you for your stay. We hope to welcome you again.';
      const pageCount = doc.getNumberOfPages();
      for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
        doc.setPage(pageIndex);
        doc.setDrawColor(...borderColor);
        doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.2);
        doc.setTextColor(...mutedText);
        doc.text(footerCopy, pageWidth / 2, pageHeight - 7.2, { align: 'center' });
      }

      const pdfOutput = doc.output('blob');
      const pdfDataUrl = doc.output('datauristring');
      return { pdfOutput, pdfDataUrl, invNum };
    } catch (pdfErr) {
      console.error('PDF Generation failed:', pdfErr);
      throw new Error('PDF Generation failed: ' + pdfErr.message);
    }
  };

  // Earliest departure date staff may enter: BACKDATE_DAYS ago (a missed
  // checkout), never before check-in — and an overdue stay's own planned date
  // always stays valid even if it is older than that.
  const earliestDepartureDate = () => {
    const checkInStr = String(selected?.checkIn || '').split('T')[0];
    let floor = getMinBackdateStr();
    if (selected?.originalCheckOut && selected.originalCheckOut < floor) floor = selected.originalCheckOut;
    return checkInStr && checkInStr > floor ? checkInStr : floor;
  };

  const handleCheckout = async () => {
    if (!selected || !guest) return;
    const departureDate = String(selected.checkOut || '').split('T')[0];
    if (departureDate && departureDate !== selected.originalCheckOut && departureDate < earliestDepartureDate()) {
      await notify(`Departure date can be set back by up to ${BACKDATE_DAYS} days only (earliest ${earliestDepartureDate()}).`);
      return;
    }
    if (departureDate === getTodayStr() && departureTime && departureTime > timeTo24h(getCurrentTimeStr())) {
      await notify('Departure time cannot be later than the current time.');
      return;
    }
    const settlementError = validateSettlement();
    if (settlementError) { await notify(settlementError); return; }
    setLoading(true);
    try {
      const guestId = guest.id || selected.guestId || selected.guest_id;
      const guestPayload = buildGuestPayload(guest, selected);
      if (!guestPayload.name || !guestPayload.phone) {
        throw new Error('Guest name and phone are required before final billing.');
      }
      const finalizedGuest = { ...guest, ...guestPayload, id: guestId };
      const finalizedSelected = {
        ...selected,
        customerType: guestPayload.customerType,
        company: guestPayload.company,
        companyGst: guestPayload.companyGst,
        billingType: currentBillingType
      };

      if (guestId) {
        await guestsApi.update(guestId, guestPayload);
      }

      const checkOutTime = timeFrom24h(departureTime) || getCurrentTimeStr();
      // Split payments total for settlement
      const finalAmount = splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      const refundAmount = Number(refund.amount) || 0;
      const allR = (finalizedSelected.room || '').split(',').map(s => s.trim()).filter(Boolean);
      const createSeparateDamageInvoice = splitDamageBill && Number(damageCharges) > 0;
      const createSeparateExtrasInvoice = splitExtrasBill && (stayOnlyExtra > 0 || (typeof selected?.extra_services === 'string' ? JSON.parse(selected?.extra_services || '[]') : (selected?.extra_services || [])).filter(s => s.settlement !== 'paid').length > 0);
      const isAnySplit = createSeparateDamageInvoice || createSeparateExtrasInvoice;

      const mainInvoiceBill = isAnySplit ? stayBill : bill;
      
      const mainInvoiceLineItems = getInvoiceLineItems(
        { ...selected, extraCharges: selected?.extraCharges || 0 },
        mainInvoiceBill,
        currentBillingType,
        {
          includeDamage: !createSeparateDamageInvoice,
          excludeExtras: splitExtrasBill || createSeparateExtrasInvoice,
          extraAmountOverride: (splitExtrasBill || createSeparateExtrasInvoice) ? 0 : stayOnlyExtra
        }
      );

      const extrasInvoiceLineItems = createSeparateExtrasInvoice ? getInvoiceLineItems(
        { ...selected, extraCharges: selected?.extraCharges || 0 },
        extrasBill,
        currentBillingType,
        {
          includeDamage: false,
          extraAmountOverride: stayOnlyExtra,
          excludeRoomStay: true
        }
      ) : [];

      const damageInvoiceLineItems = createSeparateDamageInvoice ? [{
        description: `Damage Charges${damageNote ? `: ${damageNote}` : ''}`,
        code: '-',
        details: 'Separate damage invoice',
        amount: Number(damageCharges),
        tone: 'danger'
      },
      ...(currentBillingType === 'gst' && Number(damageBill?.gstAmount) > 0 ? [
        { description: `CGST (${damageBill.gstRate / 2}%)`, code: '-', details: '-', amount: damageBill.gstAmount / 2 },
        { description: `SGST (${damageBill.gstRate / 2}%)`, code: '-', details: '-', amount: damageBill.gstAmount / 2 }
      ] : [])] : [];

      const stayT = mainInvoiceBill?.total || 0;
      const extrasT = createSeparateExtrasInvoice ? extrasBill?.total : 0;
      const damageT = createSeparateDamageInvoice ? damageBill?.total : 0;

      const splitRes = splitSettlementEntries3Way(payments, settlementRecord, stayT, extrasT, damageT);

      // 1. Generate PDF for the selection (Main/Stay)
      const { pdfOutput, pdfDataUrl, invNum } = await generatePDF(
        { ...finalizedSelected, room: selectedRooms.join(', '), roomRate: currentRoomRate, roomType: activeRoomTypes },
        finalizedGuest, hotel, mainInvoiceBill, settlementRecord, payments,
        {
          invoiceItems: mainInvoiceLineItems,
          totalAmount: mainInvoiceBill?.total,
          taxableBase: mainInvoiceBill?.subtotal,
          invoiceKindLabel: isAnySplit ? (currentBillingType === 'gst' ? 'GST Invoice' : 'Invoice') : undefined,
          settlementEntries: splitRes.stayEntries
        }
      );

      let damageInvoiceBundle = null;
      if (createSeparateDamageInvoice) {
        damageInvoiceBundle = await generatePDF(
          { ...finalizedSelected, room: selectedRooms.join(', '), roomRate: 0, roomType: activeRoomTypes, nights: 1 },
          finalizedGuest, hotel, damageBill, null, [],
          {
            invoiceNumber: `${invNum}-DMG`,
            invoiceItems: damageInvoiceLineItems,
            totalAmount: damageBill?.total,
            taxableBase: damageBill?.subtotal,
            settlementEntries: splitRes.damageEntries,
            invoiceKindLabel: `${currentBillingType === 'gst' ? 'GST Invoice' : 'Invoice'} - Damage`,
            summaryRows: [
              ['Damage Amount', fmt(Number(damageCharges) || 0)],
              ['GST Included', fmt(damageBill?.gstAmount || 0)],
              ['Total Paid', fmt(paidOnly(splitRes.damageEntries))],
              ['Settlement', 'Collected with main checkout']
            ]
          }
        );
      }

      let extrasInvoiceBundle = null;
      if (createSeparateExtrasInvoice) {
        extrasInvoiceBundle = await generatePDF(
          { ...finalizedSelected, room: selectedRooms.join(', '), roomRate: 0, roomType: activeRoomTypes, nights: 1 },
          finalizedGuest, hotel, extrasBill, null, [],
          {
            invoiceNumber: `${invNum}-EXT`,
            invoiceItems: extrasInvoiceLineItems,
            totalAmount: extrasBill?.total,
            taxableBase: extrasBill?.subtotal,
            settlementEntries: splitRes.extrasEntries,
            invoiceKindLabel: `${currentBillingType === 'gst' ? 'GST Invoice' : 'Invoice'} - Services`,
            summaryRows: [
              ['Extras Amount', fmt(Number(stayOnlyExtra) || 0)],
              ['GST Included', fmt(extrasBill?.gstAmount || 0)],
              ['Total Paid', fmt(paidOnly(splitRes.extrasEntries))],
              ['Settlement', 'Collected with main checkout']
            ]
          }
        );
      }

      if (isPartial) {
        // Partial Checkout: split reservation
        const remainingRooms = allR.filter(r => !selectedRooms.includes(r));
        const remainingRate = Math.max(0, Number(selected.roomRate) - currentRoomRate);
        const remainingAdvance = Math.max(0, paidSoFarTotal - paidSoFar);

        // Split room configurations
        const currentConfigs = selected.room_configs ? JSON.parse(selected.room_configs) : [];
        const splitConfigs = currentConfigs.map(c => {
          const roomsInC = (c.room || '').split(',').map(s => s.trim()).filter(Boolean);
          const selInC = roomsInC.filter(r => selectedRooms.includes(r));
          if (selInC.length === 0) return null;
          return { ...c, room: selInC.join(', '), roomsCount: selInC.length };
        }).filter(Boolean);

        const remConfigs = currentConfigs.map(c => {
          const roomsInC = (c.room || '').split(',').map(s => s.trim()).filter(Boolean);
          const remInC = roomsInC.filter(r => remainingRooms.includes(r));
          if (remInC.length === 0) return null;
          return { ...c, room: remInC.join(', '), roomsCount: remInC.length };
        }).filter(Boolean);

        let splitType = splitConfigs.map(c => c.roomType + (c.roomsCount > 1 ? ` x${c.roomsCount}` : '')).join(' + ');
        let remType = remConfigs.map(c => c.roomType + (c.roomsCount > 1 ? ` x${c.roomsCount}` : '')).join(' + ');

        if (!splitType) splitType = activeRoomTypes || selected.roomType || selected.room_type;
        if (!remType) {
          const allTypes = (selected.roomType || selected.room_type || '').split('+').map(s => s.trim());
          if (allTypes.length === allR.length) {
            remType = remainingRooms.map(rn => allTypes[allR.indexOf(rn)]).filter(Boolean).join(' + ');
          } else {
            remType = selected.roomType || selected.room_type;
          }
        }

        // Update original to exclude checked-out rooms and keep remaining advance
        await reservationsApi.update(selected.id, {
          room: remainingRooms.join(', '),
          roomType: remType,
          roomRate: remainingRate,
          advancePaid: remainingAdvance,
          roomConfigs: remConfigs,
          customerType: guestPayload.customerType,
          company: guestPayload.company,
          companyGst: guestPayload.companyGst,
          billingType: currentBillingType
        });

        const splitResId = genId('RS');
        await reservationsApi.create({
          ...finalizedSelected,
          id: splitResId,
          hotelId: hotel.id,
          guestId,
          room: selectedRooms.join(', '),
          roomType: splitType,
          roomRate: currentRoomRate,
          status: 'checked-out',
          checkOutTime,
          checkOut: selected.checkOut,
          nights: selected.nights,
          extraCharges: Number(extraAtCheckout) - (Number(discountAmount) || 0),
          damageCharges: Number(damageCharges) || 0,
          damageNote: damageNote || '',
          totalAmount: bill.total,
          advancePaid: paidSoFar, // Attributed portion
          roomConfigs: splitConfigs,
          customerType: guestPayload.customerType,
          company: guestPayload.company,
          companyGst: guestPayload.companyGst,
          billingType: currentBillingType
        });

        if (finalAmount > 0) {
          for (const sp of splitPayments) {
            const spAmt = Number(sp.amount) || 0;
            if (spAmt <= 0) continue;
            await paymentsApi.create({
              reservationId: splitResId, hotelId: hotel.id,
              type: sp.type, amount: spAmt,
              last5: ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(sp.type) ? sp.last5 : null,
              note: sp.note?.trim() || 'Partial checkout settlement'
            });
          }
        }

        if (refundAmount > 0) {
          await paymentsApi.create({
            reservationId: splitResId, hotelId: hotel.id,
            type: refund.type, amount: -refundAmount,
            last5: ['upi', 'credit_card', 'debit_card', 'bank_transfer', 'cheque'].includes(refund.type) ? refund.last5 : null,
            note: refund.note || 'Advance refund'
          });
        }

        if (payLaterAmount > 0) {
          await companyDuesApi.create({
            hotelId: hotel.id, reservationId: splitResId, amount: payLaterAmount,
            contactName: payLater.contactName, contactPhone: payLater.contactPhone, promisedNote: payLater.promisedNote
          });
        }

        await invoicesApi.create({
          reservationId: splitResId, guestId, hotelId: hotel.id,
          invoiceNumber: invNum, totalAmount: bill.total,
          gstAmount: bill.gstAmount, billingType: currentBillingType,
          pdfDataUrl: pdfDataUrl, pdfUrl: pdfDataUrl
        });

      } else {
        // FULL Checkout
        if (finalAmount > 0) {
          for (const sp of splitPayments) {
            const spAmt = Number(sp.amount) || 0;
            if (spAmt <= 0) continue;
            await paymentsApi.create({
              reservationId: selected.id, hotelId: hotel.id,
              type: sp.type, amount: spAmt,
              last5: ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(sp.type) ? sp.last5 : null,
              note: sp.note?.trim() || 'Final settlement'
            });
          }
        }

        if (refundAmount > 0) {
          await paymentsApi.create({
            reservationId: selected.id, hotelId: hotel.id,
            type: refund.type, amount: -refundAmount,
            last5: ['upi', 'credit_card', 'debit_card', 'bank_transfer', 'cheque'].includes(refund.type) ? refund.last5 : null,
            note: refund.note || 'Advance refund'
          });
        }

        await reservationsApi.update(selected.id, {
          status: 'checked-out',
          checkOutTime,
          checkOut: selected.checkOut,
          nights: selected.nights,
          extraCharges: totalExtra - (Number(discountAmount) || 0), // DB stores net extra
          damageCharges: Number(damageCharges) || 0,
          damageNote: damageNote || '',
          totalAmount: bill.total,
          customerType: guestPayload.customerType,
          company: guestPayload.company,
          companyGst: guestPayload.companyGst,
          billingType: currentBillingType
        });

        if (payLaterAmount > 0) {
          await companyDuesApi.create({
            hotelId: hotel.id, reservationId: selected.id, amount: payLaterAmount,
            contactName: payLater.contactName, contactPhone: payLater.contactPhone, promisedNote: payLater.promisedNote
          });
        }

        await invoicesApi.create({
          reservationId: selected.id, guestId, hotelId: hotel.id,
          invoiceNumber: invNum, totalAmount: bill.total,
          gstAmount: bill.gstAmount, billingType: currentBillingType,
          pdfDataUrl: pdfDataUrl, pdfUrl: pdfDataUrl
        });
      }

      const dueNote = payLaterAmount > 0 ? ` ${fmt(payLaterAmount)} due from ${companyPayload?.company} — see Cash Management → Company Dues.` : '';
      onSuccess?.((isPartial ? `Partial checkout for Room ${selectedRooms.join(', ')} complete!` : `Checkout for ${selected.room} complete!`) + dueNote);

      const url = URL.createObjectURL(pdfOutput);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invNum}_${(finalizedGuest.name || 'Guest').replace(/\s+/g, '_')}.pdf`;
      link.click();

      if (damageInvoiceBundle) {
        const damageUrl = URL.createObjectURL(damageInvoiceBundle.pdfOutput);
        const damageLink = document.createElement('a');
        damageLink.href = damageUrl;
        damageLink.download = `Invoice_${invNum}-DMG_${(finalizedGuest.name || 'Guest').replace(/\s+/g, '_')}.pdf`;
        damageLink.click();
      }

      if (extrasInvoiceBundle) {
        const extrasUrl = URL.createObjectURL(extrasInvoiceBundle.pdfOutput);
        const extrasLink = document.createElement('a');
        extrasLink.href = extrasUrl;
        extrasLink.download = `Invoice_${invNum}-EXT_${(finalizedGuest.name || 'Guest').replace(/\s+/g, '_')}.pdf`;
        extrasLink.click();
      }

      setSelected(null); setGuest(null); loadActive();
    } catch (err) {
      console.error(err);
      await notify('Checkout failed: ' + err.message);
    }
    setLoading(false);
  };

  // Share handlers
  const handleWhatsAppShare = async () => {
    const targetPhone = guest?.phone || selected?.guest_phone || selected?.phone || '';
    if (!targetPhone) return await notify('No phone number available for this guest.');
    
    // Auto-download PDF invoice for staff to easily attach in WhatsApp
    try {
      await handleSavePDF();
    } catch (_) {}

    const hasSeparateExtras = splitExtrasBill && stayOnlyExtra > 0;
    const hasSeparateDamage = splitDamageBill && Number(damageCharges) > 0;
    const hasSplit = hasSeparateExtras || hasSeparateDamage;

    const extrasT = hasSeparateExtras ? stayOnlyExtra : 0;
    const damageT = hasSeparateDamage ? Number(damageCharges) : 0;
    const stayT = currentTotal - extrasT - damageT;

    openWhatsAppInvoiceShare({
      phone: targetPhone,
      hotelName: hotel?.name,
      guestName: getTextValue(guest?.name),
      invoiceNumber: previewInvNum,
      reservationId: selected?.id,
      totalAmount: currentTotal,
      room: selectedRooms.join(', '),
      checkIn: selected?.checkIn,
      checkOut: selected?.checkOut,
      isCheckBill: !checklist.billReviewed,
      hasSeparateBills: hasSplit,
      stayTotal: stayT,
      extrasTotal: extrasT,
      damageTotal: damageT
    });
  };


  const previewUsesSeparateDamage = splitDamageBill && Number(damageCharges) > 0;
  const previewUsesSeparateExtras = splitExtrasBill && (stayOnlyExtra > 0 || (typeof selected?.extra_services === 'string' ? JSON.parse(selected?.extra_services || '[]') : (selected?.extra_services || [])).filter(s => s.settlement !== 'paid').length > 0);
  
  const activePreviewTab = (previewTab === 'damage' && !previewUsesSeparateDamage) || (previewTab === 'extras' && !previewUsesSeparateExtras) ? 'stay' : previewTab;
  const previewBillData = activePreviewTab === 'damage' ? damageBill : (activePreviewTab === 'extras' ? extrasBill : (previewUsesSeparateDamage || previewUsesSeparateExtras ? stayBill : bill));

  const previewReservation = selected ? { ...selected, extraCharges: selected?.extraCharges || 0 } : null;

  let previewInvoiceItems = [];
  if (previewReservation && previewBillData) {
    if (activePreviewTab === 'damage') {
      previewInvoiceItems = [{
        description: `Damage Charges${damageNote ? `: ${damageNote}` : ''}`,
        code: '-', details: 'Separate damage invoice', amount: Number(damageCharges), tone: 'danger'
      }, ...(currentBillingType === 'gst' && Number(damageBill?.gstAmount) > 0 ? [
        { description: `CGST (${damageBill.gstRate / 2}%)`, code: '-', details: '-', amount: damageBill.gstAmount / 2 },
        { description: `SGST (${damageBill.gstRate / 2}%)`, code: '-', details: '-', amount: damageBill.gstAmount / 2 }
      ] : [])];
    } else if (activePreviewTab === 'extras') {
      previewInvoiceItems = getInvoiceLineItems(previewReservation, previewBillData, currentBillingType, {
          includeDamage: false, extraAmountOverride: stayOnlyExtra, excludeRoomStay: true
      });
    } else {
      previewInvoiceItems = getInvoiceLineItems(previewReservation, previewBillData, currentBillingType, {
          includeDamage: !previewUsesSeparateDamage,
          excludeExtras: splitExtrasBill || previewUsesSeparateExtras,
          extraAmountOverride: (splitExtrasBill || previewUsesSeparateExtras) ? 0 : stayOnlyExtra
      });
    }
  }

  const previewDocumentLabelRaw = checklist.billReviewed ? (currentBillingType === 'gst' ? 'GST Invoice' : 'Invoice') : 'Check Bill';
  const previewDocumentLabel = activePreviewTab === 'damage' ? `${previewDocumentLabelRaw} - Damage` : (activePreviewTab === 'extras' ? `${previewDocumentLabelRaw} - Services` : previewDocumentLabelRaw);
  const invoiceNumberSuffix = activePreviewTab === 'damage' ? '-DMG' : (activePreviewTab === 'extras' ? '-EXT' : '');
  const previewInvNum = invNumPreview ? `${invNumPreview}${invoiceNumberSuffix}` : '';

  const previewAnySplit = previewUsesSeparateDamage || previewUsesSeparateExtras;
  const stayT = stayBill?.total || 0;
  const extrasT = previewUsesSeparateExtras ? extrasBill?.total : 0;
  const damageT = previewUsesSeparateDamage ? damageBill?.total : 0;

  const previewSplitRes = splitSettlementEntries3Way(payments, settlementRecord, stayT, extrasT, damageT);

  let previewSettlementEntries = [];
  let previewTotalPaid = 0;

  if (activePreviewTab === 'stay') {
    previewSettlementEntries = previewAnySplit ? (previewSplitRes?.stayEntries || []) : getSettlementEntries(payments, settlementRecord);
  } else if (activePreviewTab === 'damage') {
    previewSettlementEntries = previewSplitRes.damageEntries;
  } else if (activePreviewTab === 'extras') {
    previewSettlementEntries = previewSplitRes.extrasEntries;
  }
  previewTotalPaid = paidOnly(previewSettlementEntries);

  const previewGuestName = formatDisplayName(guest?.salutation, getTextValue(guest?.name));
  const previewGuestAddress = getTextValue(guest?.address) ? toTitleCase(getTextValue(guest?.address)) : 'NA';
  const previewHotelName = getHotelNameText(billDesign?.hotelName) || getHotelNameText(hotel) || 'Hotel';
  const previewHotelAddress = getHotelAddressText(billDesign?.hotelAddress) || getHotelAddressText(hotel) || 'NA';
  const previewHotelPhone = getHotelPhoneText(hotel) || 'NA';
  const previewCompanyName = getTextValue(selected?.company) || getTextValue(guest?.company) || 'NA';
  const previewCompanyGst = getTextValue(selected?.companyGst) || getTextValue(guest?.companyGst) || 'NA';
  const previewTermsText = getTextValue(billDesign?.termsAndConditions) || [
    "1. All guests must provide valid ID proof during check-in.",
    "2. Management is not responsible for any loss of valuable belongings.",
    "3. Any damage to hotel property will be charged to the guest.",
    "4. Final bill must be settled in full before check-out."
  ].join('\n');
  const previewSignatureLabel = getTextValue(billDesign?.signatureLabel) || 'Authorized Signatory / Seal';
  const previewFooterText = getTextValue(billDesign?.footerText) || 'Thank you for your stay. We hope to welcome you again.';
  const previewIssuedAt = new Date();
  const previewInvoiceDate = previewIssuedAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const previewInvoiceTime = previewIssuedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const previewTaxableBase = Number(previewBillData?.subtotal ?? (previewBillData?.total || 0) - (previewBillData?.gstAmount || 0));
  const previewDisplayTotal = Number(previewBillData?.total || 0);

  const isCorporate = selected?.customerType === 'corporate';

  const previewPdfOptions = {
    hideInvoiceNumber: !checklist.billReviewed,
    hideHotelTaxNumber: true,
    documentLabel: previewDocumentLabel,
    invoiceNumber: previewInvNum,
    invoiceItems: previewInvoiceItems,
    totalAmount: previewBillData?.total,
    taxableBase: previewTaxableBase,
    settlementEntries: previewSettlementEntries,
    invoiceKindLabel: previewDocumentLabel
  };

  if (activePreviewTab === 'damage') {
    previewPdfOptions.summaryRows = [
      ['Damage Amount', fmt(Number(damageCharges) || 0)],
      ['GST Included', fmt(previewBillData?.gstAmount || 0)],
      ['Total Paid', fmt(previewTotalPaid)],
      ['Settlement', 'Collected with main checkout']
    ];
  } else if (activePreviewTab === 'extras') {
    previewPdfOptions.summaryRows = [
      ['Extras Amount', fmt(Number(stayOnlyExtra) || 0)],
      ['GST Included', fmt(previewBillData?.gstAmount || 0)],
      ['Total Paid', fmt(previewTotalPaid)],
      ['Settlement', 'Collected with main checkout']
    ];
  }

  const handleEmailShare = async () => {
    try {
      await generatePDF(previewReservation, guest, hotel, previewBillData, settlementRecord, payments, previewPdfOptions);
      const safeGuestName = getTextValue(guest?.name) || 'Guest';
      const safeHotelName = getHotelNameText(hotel) || 'Hotel';
      const subject = encodeURIComponent(`${previewDocumentLabel} from ${safeHotelName} — Stay ${selected?.checkIn} to ${selected?.checkOut}`);
      const body = encodeURIComponent(`Dear ${safeGuestName},\n\nPlease review this ${previewDocumentLabel.toLowerCase()}.\nTotal Amount: ${fmt(previewDisplayTotal)}\n\nKindly confirm the details before final billing.\n\n— ${safeHotelName}`);
      window.location.href = `mailto:${guest?.email || ''}?subject=${subject}&body=${body}`;
    } catch (e) { await notify('Could not prepare email: ' + e.message); }
  };

  const handleSavePDF = async () => {
    try {
      const { pdfOutput } = await generatePDF(previewReservation, guest, hotel, previewBillData, settlementRecord, payments, previewPdfOptions);
      const url = URL.createObjectURL(pdfOutput);
      const link = document.createElement('a');
      link.href = url;
      const safeGuestName = (getTextValue(guest?.name) || 'Guest').replace(/\s+/g, '_');
      link.download = `${previewDocumentLabel.replace(/\s+/g, '_')}_${safeGuestName}.pdf`;
      link.click();
    } catch (e) { await notify('Could not save PDF: ' + e.message); }
  };

  const handlePrintPreview = async () => {
    try {
      const { pdfOutput } = await generatePDF(previewReservation, guest, hotel, previewBillData, settlementRecord, payments, previewPdfOptions);
      const url = URL.createObjectURL(pdfOutput);
      const printWindow = window.open(url, '_blank');
      if (!printWindow) {
        await notify('Popup blocked. Please allow popups to print the preview bill.');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        return;
      }

      const tryPrint = () => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (err) {
          console.error('Print preview failed:', err);
        }
      };

      printWindow.onload = tryPrint;
      setTimeout(tryPrint, 800);
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (e) {
      await notify('Could not print preview bill: ' + e.message);
    }
  };

  return (
    <div>
      <div className="grid-2">
        {/* LEFT: Active rooms list */}
        <div className="panel" style={{ maxHeight: '78vh', overflowY: 'auto' }}>
          <div className="panel-head">
            <span className="panel-title">Checkout Selection</span>
            <span className="panel-sub">By Room Number</span>
          </div>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <input
              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 7, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font-b)', fontSize: 12, outline: 'none' }}
              placeholder="Room #, Name or Phone"
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>
          {filtered.length === 0 ? (
            <div className="empty"><div className="empty-icon">🛏️</div>No matching stays</div>
          ) : filtered.map(r => (
            <div key={r.id} onClick={() => selectRes(r)} style={{
              padding: '13px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
              background: selected?.id === r.id ? 'var(--s2)' : 'transparent',
              borderLeft: selected?.id === r.id ? `4px solid ${hotel.color}` : '4px solid transparent',
              transition: 'all 0.15s',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: hotel.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                    {(r.room || '').split(',')[0].trim() || '—'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.guestName}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{r.guestPhone} · Out: {fmtDate(r.checkOut)}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>Current Due</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--danger)' }}>{fmt(calcBill(r, r.billingType || (r.customerType === 'corporate' ? 'gst' : 'non-gst'), hotel?.gstRate).balanceDue)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: Checkout details */}
        <div>
          {!selected ? (
            <div className="panel" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', opacity: 0.5 }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🚪</div>
                <div>Select a room from the left to start checkout</div>
              </div>
            </div>
          ) : (
            <>
              {/* Guest info */}
              <div className="panel" style={{ marginBottom: 16 }}>
                <div className="panel-head">
                  <span className="panel-title">{getTextValue(guest?.name) || 'Guest'}</span>
                  <span className={`badge ${isCorporate ? 'b-amber' : 'b-blue'}`}>{isCorporate ? 'Corporate' : 'Individual'}</span>
                </div>
                <div style={{ padding: '14px 18px', fontSize: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
                    <div><span style={{ color: 'var(--muted)' }}>Phone: </span>{guest?.phone}</div>
                    <div style={{ gridColumn: '1 / -1', marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Stay Details / Select Checkout</div>
                      </div>
                      {(selected.room || '').split(',').map((rNum, idx) => {
                        const cleanNum = rNum.trim();
                        const isChecked = selectedRooms.includes(cleanNum);
                        return (
                          <div key={idx}
                            onClick={() => {
                              setSelectedRooms(prev => isChecked
                                ? (prev.length > 1 ? prev.filter(r => r !== cleanNum) : prev)
                                : [...prev, cleanNum]);
                            }}
                            style={{
                              background: isChecked ? 'var(--s1)' : 'transparent',
                              border: `1px solid ${isChecked ? 'var(--accent)' : 'var(--border)'}`,
                              borderRadius: 8, padding: '10px 14px', marginBottom: 6, cursor: 'pointer',
                              transition: 'all 0.1s'
                            }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <input type="checkbox" checked={isChecked} onChange={() => { }} style={{ width: 16, height: 16 }} />
                                <span style={{ fontSize: 14, fontWeight: 800, color: isChecked ? 'var(--accent)' : '#aaa' }}>Room {cleanNum}</span>
                              </div>
                              <span style={{ fontSize: 11, color: 'var(--muted2)' }}>{selected.roomType}</span>
                            </div>
                            <div style={{ fontSize: 12, marginTop: 4, display: 'flex', gap: 15, opacity: isChecked ? 1 : 0.4 }}>
                              <span>📅 <strong>In:</strong> {fmtDate(selected.checkIn)}</span>
                              <span>🕒 <strong>At:</strong> {selected.check_in_time || selected.checkInTime || '—'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {isPartial && (
                      <div style={{ gridColumn: '1 / -1', background: '#fff', border: '1px dashed var(--border2)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 10 }}>Advance Management (Partial Checkout)</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {['full', 'half', 'zero', 'custom'].map(m => (
                            <button key={m} onClick={() => setAdvanceMode(m)} style={{
                              flex: 1, padding: '8px', fontSize: 11, fontWeight: 700, borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer',
                              background: advanceMode === m ? 'var(--accent)' : 'transparent',
                              color: advanceMode === m ? '#fff' : 'var(--text)',
                              transition: 'all 0.1s'
                            }}>
                              {m.toUpperCase()} ({m === 'full' ? fmt(paidSoFarTotal) : m === 'half' ? fmt(paidSoFarTotal / 2) : m === 'zero' ? '0' : 'Other'})
                            </button>
                          ))}
                        </div>
                        {advanceMode === 'custom' && (
                          <div style={{ marginTop: 10 }}>
                            <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Amount to apply to THIS checkout:</label>
                            <input type="number" value={customAdvance} onChange={e => setCustomAdvance(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)' }} />
                          </div>
                        )}
                        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--muted2)', textAlign: 'center' }}>
                          Remaining {fmt(paidSoFarTotal - paidSoFar)} will stay with the active reservation.
                        </div>
                      </div>
                    )}
                    {(selected.notes || '').trim() && (
                      <div style={{ gridColumn: '1 / -1', marginTop: 5, padding: '8px 12px', background: 'var(--s1)', borderRadius: 7, border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 4 }}>Notes from Check-in</div>
                        <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{selected.notes}</div>
                      </div>
                    )}
                    <div style={{ gridColumn: '1 / -1', marginTop: 5, padding: '8px 12px', background: 'rgba(232,184,109,0.1)', borderRadius: 7, border: '1px solid rgba(232,184,109,0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>Departure Schedule</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--teal)' }}>{selected.nights} {stayUnitLabel} Stay</span>
                      </div>
                      <div style={{ fontSize: 13, marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        🏁 <strong>Check-out:</strong>
                        <input
                          type="date"
                          min={earliestDepartureDate()}
                          value={String(selected.checkOut || '').split('T')[0]}
                          onChange={e => {
                            const newOut = e.target.value;
                            if (newOut) {
                              if (!departureTimeTouched) setDepartureTime(newOut < getTodayStr() ? '12:00' : timeTo24h(getCurrentTimeStr()));
                              const calcNights = (start, end) => {
                                const diff = new Date(end) - new Date(start);
                                return Math.max(1, Math.round(diff / 86400000));
                              };
                              const updatedNights = calcNights(selected.checkIn, newOut);
                              setSelected({ ...selected, checkOut: newOut, nights: updatedNights });
                            }
                          }}
                          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--accent)', borderRadius: 6, fontWeight: 700, color: 'var(--text)', background: 'var(--bg)', flex: 1 }}
                        />
                      </div>
                      <div style={{ fontSize: 13, marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        🕑 <strong>Departure time:</strong>
                        <input
                          type="time"
                          value={departureTime}
                          onChange={e => { setDepartureTime(e.target.value); setDepartureTimeTouched(true); }}
                          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--accent)', borderRadius: 6, fontWeight: 700, color: 'var(--text)', background: 'var(--bg)', flex: 1 }}
                        />
                      </div>
                      {String(selected.checkOut || '').split('T')[0] < getTodayStr() && (
                        <div style={{ fontSize: 11, marginTop: 6, padding: '6px 8px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', color: '#1d4ed8', fontWeight: 600 }}>
                          ⏪ Missed checkout — billing as departed on {fmtDate(selected.checkOut)} at {timeFrom24h(departureTime) || '—'}. Change the date or time above if the guest left at a different time.
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6, fontStyle: 'italic' }}>
                        * Edit check-out date to recalculate bill for early departures, or set it up to {BACKDATE_DAYS} days back for a missed checkout
                      </div>
                    </div>
                    {isCorporate && (
                      <div style={{ gridColumn: '1 / -1', marginTop: 10, borderTop: '1px dashed var(--border)', paddingTop: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                          <div className="field">
                            <label style={{ fontSize: 10 }}>Company Name</label>
                            <input value={getTextValue(selected.company) || getTextValue(guest?.company) || ''} onChange={e => handleGuestFieldChange('company', e.target.value, true)} style={{ padding: '6px 10px', fontSize: 13 }} />
                          </div>
                          <div className="field">
                            <label style={{ fontSize: 10 }}>Company GST Number</label>
                            <input value={getTextValue(selected.companyGst) || getTextValue(guest?.companyGst) || ''} onChange={e => handleGuestFieldChange('companyGst', e.target.value, true)} style={{ padding: '6px 10px', fontSize: 13 }} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Settlement Bill */}
              <div className="panel" style={{ marginBottom: 16 }}>
                <div className="panel-head"><span className="panel-title">Settlement Summary</span></div>
                <div style={{ padding: '16px 18px' }}>
                    <div className="bill-box" style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                      <span>Room Stay Charges{stayPricingMeta.hasSpecialRate ? ' - Special Rate' : ''} ({selectedRooms.length} Room{selectedRooms.length > 1 ? 's' : ''} × {selected.nights} {stayUnitLabel})</span>
                      <span style={{ fontWeight: 700 }}>{fmt(currentRoomRate * selected.nights)}</span>
                    </div>
                    {stayPricingMeta.note && (
                      <div style={{ marginTop: -2, marginBottom: 8, fontSize: 11, color: 'var(--teal)', fontWeight: 700 }}>
                        {stayPricingMeta.note}
                      </div>
                    )}
                    {selected.extraCharges > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>Service / Extra Charges</span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 11, cursor: 'pointer' }}>
                            <input type="checkbox" checked={splitExtrasBill} onChange={e => setSplitExtrasBill(e.target.checked)} />
                            Print on separate bill
                          </label>
                        </div>
                        <span style={{ fontWeight: 700 }}>{fmt(selected.extraCharges)}</span>
                      </div>
                    )}

                    {/* Additional charges at checkout */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 13 }}>Additional Charges at Checkout</span>
                      <input type="number" value={extraAtCheckout} onChange={e => {
                        setExtraAtCheckout(e.target.value);
                      }}
                        style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', width: 100, textAlign: 'right', fontWeight: 700 }} />
                    </div>

                    {/* Late Checkout Auto-Fee Alert */}
                    {(() => {
                      const lateInfo = computeLateCheckoutInfo();
                      if (!lateInfo.isLate) return null;
                      return (
                        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                            <div>
                              <div style={{ fontWeight: 800, color: '#f57f17', fontSize: 13 }}>
                                ⏰ Late Checkout Detected ({lateInfo.hoursLate}h past {lateInfo.cutoffStr} cutoff)
                              </div>
                              <div style={{ fontSize: 11, color: '#7f6000', marginTop: 3 }}>
                                {lateInfo.isHalfDay ? `Passes ${hotel?.late_checkout_half_day_hours || 4}h threshold → Half-Day Rate (₹${lateInfo.fee})` : `Calculated at ₹${hotel?.late_checkout_hourly_rate || 200}/hour (${lateInfo.hoursLate} hrs)`}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{ background: '#f57f17', color: 'white', fontWeight: 800, borderRadius: 8, padding: '6px 14px', flexShrink: 0, border: 'none', cursor: 'pointer' }}
                              onClick={() => {
                                setDamageCharges(String(lateInfo.fee));
                                setDamageNote(`Late Checkout Fee (${lateInfo.hoursLate} hrs late past ${lateInfo.cutoffStr} cutoff)`);
                              }}
                            >
                              ➕ Apply ₹{lateInfo.fee} Fee
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Damage Charges */}
                    <div style={{ background: 'rgba(211,62,62,0.05)', border: '1px dashed rgba(211,62,62,0.3)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)', marginBottom: 8 }}>⚠️ Damage Charges (optional)</div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <input type="number" placeholder="Amount ₹" value={damageCharges} onChange={e => {
                          setDamageCharges(e.target.value);
                        }}
                          style={{ border: '1px solid rgba(211,62,62,0.3)', borderRadius: 6, padding: '6px 8px', width: 110, fontWeight: 700, color: 'var(--danger)' }} />
                        <input placeholder="Description (e.g. Broken mirror)" value={damageNote} onChange={e => setDamageNote(e.target.value)}
                          style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', fontSize: 12 }} />
                      </div>
                      {Number(damageCharges) > 0 && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, fontWeight: 700, color: 'var(--text)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={splitDamageBill} onChange={e => setSplitDamageBill(e.target.checked)} />
                          Create separate damage bill
                        </label>
                      )}
                    </div>

                    {/* Discount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: 'var(--success)', fontWeight: 700 }}>Discount Allowed (₹)</span>
                      <input type="number" value={discountAmount} onChange={e => {
                        setDiscountAmount(e.target.value);
                      }}
                        style={{ border: '1px solid var(--success)', borderRadius: 6, padding: '4px 8px', width: 100, textAlign: 'right', fontWeight: 700, color: 'var(--success)', background: 'rgba(39, 174, 96, 0.05)' }} />
                    </div>

                    {isCorporate && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: 'var(--muted)' }}>
                        <span>GST ({bill?.gstRate}%)</span>
                        <span>{fmt(bill?.gstAmount)}</span>
                      </div>
                    )}
                    <div style={{ height: 1.5, background: 'var(--border)', margin: '12px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800 }}>
                      <span>Grand Total</span>
                      <span style={{ color: hotel.color }}>{fmt(currentTotal)}</span>
                    </div>
                    <div style={{ height: 1.5, background: 'var(--border)', margin: '12px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--success)' }}>
                      <span>Total Paid (Advance + Earlier)</span>
                      <span style={{ fontWeight: 700 }}>- {fmt(paidSoFar)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 900, marginTop: 10, color: 'var(--danger)' }}>
                      <span>{refundDue > 0 ? 'REFUND TO CUSTOMER' : 'FINAL SETTLEMENT DUE'}</span>
                      <span style={{ color: refundDue > 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {fmt(refundDue > 0 ? refundDue : Math.max(0, currentTotal - paidSoFar))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final Payment */}
              <div className="panel">
                <div className="panel-head"><span className="panel-title">Complete Checkout & Print Bill</span></div>
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ marginBottom: 20, fontSize: 13, fontWeight: 700, color: refundDue > 0 ? 'var(--success)' : 'var(--text)' }}>
                    {refundDue > 0 ? 'Refund Details' : 'Payment Collection Details'}
                  </div>
                  {splitDamageBill && Number(damageCharges) > 0 && (
                    <div style={{ marginBottom: 14, padding: '10px 12px', background: 'rgba(232,184,109,0.12)', border: '1px solid rgba(232,184,109,0.28)', borderRadius: 8, fontSize: 12, color: 'var(--text)' }}>
                      Two invoices will be created:
                      Stay bill `#{invNumPreview || '...'}` and damage bill `#{(invNumPreview || '...') + '-DMG'}`.
                    </div>
                  )}
                  {/* ─── SPLIT PAYMENT ROWS ─── */}
                  {refundDue <= 0 && (
                    <div>
                      {finalBalance > 0.5 && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', alignSelf: 'center' }}>Quick fill {fmt(finalBalance)}:</span>
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => fillFullPayment('cash')}>💵 Full Cash</button>
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => fillFullPayment('upi')}>📱 Full UPI</button>
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => fillFullPayment('credit_card')}>💳 Full Card</button>
                        </div>
                      )}
                      {splitPayments.map((sp, idx) => {
                        const isNonCash = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(sp.type);
                        const splitTotal = splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                        const remaining = Math.max(0, finalBalance - splitTotal + (Number(sp.amount) || 0));
                        return (
                          <div key={sp.id} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 10, position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                                {idx === 0 ? '💳 Payment 1' : `💳 Payment ${idx + 1}`}
                              </span>
                              {splitPayments.length > 1 && (
                                <button type="button" onClick={() => setSplitPayments(p => p.filter(r => r.id !== sp.id))}
                                  style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>✕ Remove</button>
                              )}
                            </div>
                            {/* Payment type buttons */}
                            <div className="pay-type-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 12 }}>
                              {PAYMENT_TYPES.map(pt => (
                                <button key={pt.value} type="button"
                                  className={`pay-type-btn ${sp.type === pt.value ? 'selected' : ''}`}
                                  onClick={() => setSplitPayments(prev => prev.map(r => r.id === sp.id ? { ...r, type: pt.value, last5: '' } : r))}
                                  style={{ padding: '8px 4px', fontSize: 10, border: sp.type === pt.value ? `2px solid ${hotel.color}` : '1px solid var(--border)' }}>
                                  <div style={{ fontSize: 16 }}>{pt.icon}</div>
                                  <div style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>{pt.label}</div>
                                </button>
                              ))}
                            </div>
                            <div className="form-row-2">
                              <div className="field">
                                <label>Amount (₹) <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>Remaining: {fmt(remaining)}</span></label>
                                <input type="number" min="0" step="1"
                                  value={sp.amount}
                                  onChange={e => {
                                    const val = Number(e.target.value) || 0;
                                    const otherTotal = splitPayments.filter(r => r.id !== sp.id).reduce((s, p) => s + (Number(p.amount) || 0), 0);
                                    if (val > finalBalance - otherTotal + 0.01) return;
                                    setSplitPayments(prev => prev.map(r => r.id === sp.id ? { ...r, amount: e.target.value } : r));
                                  }}
                                  style={{ fontSize: 15, fontWeight: 700 }} />
                              </div>
                              {isNonCash && (
                                <div className="field">
                                  <label>
                                    {sp.type === 'cheque' ? 'Cheque No. (Last 5)' : sp.type === 'bank_transfer' ? 'Transaction ID (Last 5)' : 'Ref. / UPI / Card (Last 5)'}
                                    <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>
                                  </label>
                                  <input
                                    value={sp.last5}
                                    onChange={e => setSplitPayments(prev => prev.map(r => r.id === sp.id ? { ...r, last5: e.target.value.replace(/\D/g, '').slice(0, 5) } : r))}
                                    placeholder="e.g. 48291"
                                    maxLength={5}
                                    required
                                    style={{ borderColor: isNonCash && !sp.last5 ? 'var(--danger)' : '' }}
                                  />
                                  {isNonCash && !sp.last5 && (
                                    <span style={{ fontSize: 10, color: 'var(--danger)', marginTop: 2, display: 'block' }}>Reference required for non-cash payment</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
                              <label>Remarks <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
                              <input
                                value={sp.note}
                                onChange={e => setSplitPayments(prev => prev.map(r => r.id === sp.id ? { ...r, note: e.target.value } : r))}
                                placeholder="e.g. paid by relative"
                              />
                            </div>
                          </div>
                        );
                      })}

                      {/* Total vs balance indicator */}
                      {(() => {
                        const covered = splitTotal + payLaterAmount;
                        const diff = finalBalance - covered;
                        return (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, marginBottom: 10,
                            background: Math.abs(diff) < 0.5 ? 'rgba(109,191,148,0.12)' : 'rgba(232,184,109,0.10)',
                            border: `1px solid ${Math.abs(diff) < 0.5 ? 'rgba(109,191,148,0.3)' : 'rgba(232,184,109,0.3)'}`
                          }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>Split Total</span>
                            <span style={{ fontWeight: 800, fontSize: 14, color: Math.abs(diff) < 0.5 ? 'var(--success)' : 'var(--gold)' }}>
                              {fmt(covered)} / {fmt(finalBalance)}
                              {diff > 0.5 && <span style={{ fontSize: 10, marginLeft: 6, fontWeight: 400 }}>({fmt(diff)} remaining)</span>}
                              {diff < -0.5 && <span style={{ fontSize: 10, marginLeft: 6, fontWeight: 400 }}>({fmt(Math.abs(diff))} over)</span>}
                              {Math.abs(diff) < 0.5 && <span style={{ fontSize: 10, marginLeft: 6 }}>✅ Fully covered{payLaterAmount > 0 ? ` (${fmt(payLaterAmount)} pay later)` : ''}</span>}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Add another payment method */}
                      {splitTotal < finalBalance - 0.5 && !payLaterActive && (
                        <button type="button"
                          onClick={() => {
                            const paid = splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                            setSplitPayments(prev => [...prev, { ...mkPayRow(), amount: String(Math.max(0, finalBalance - paid)) }]);
                          }}
                          style={{ width: '100%', padding: '10px', border: '2px dashed var(--border)', borderRadius: 8, background: 'transparent', color: 'var(--accent)', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginBottom: 10 }}>
                          + Add Another Payment Method
                        </button>
                      )}

                      {/* Company Pay Later — GST company bills only */}
                      {finalBalance > 0.5 && (payLaterEligible ? (
                        <div style={{ border: `1.5px solid ${payLaterActive ? 'var(--gold)' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 10, background: payLaterActive ? 'rgba(232,184,109,0.08)' : 'transparent' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                            <input type="checkbox" checked={payLater.enabled} onChange={e => setPayLater(pl => ({ ...pl, enabled: e.target.checked }))} />
                            💼 Company Pay Later — {companyPayload?.company} will pay the rest
                          </label>
                          {payLaterActive && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 13, marginBottom: 8 }}>
                                Due from company: <strong style={{ color: 'var(--gold)' }}>{fmt(payLaterAmount)}</strong>
                                <span style={{ color: 'var(--muted)', fontSize: 11 }}> (bill balance minus what is paid now; overdue after 24 hours)</span>
                              </div>
                              <div className="form-row-2">
                                <div className="field">
                                  <label>Contact Person *</label>
                                  <input value={payLater.contactName} onChange={e => setPayLater(pl => ({ ...pl, contactName: e.target.value }))} placeholder="Who will pay" />
                                </div>
                                <div className="field">
                                  <label>Contact Phone *</label>
                                  <input value={payLater.contactPhone} maxLength={10} onChange={e => setPayLater(pl => ({ ...pl, contactPhone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit mobile" />
                                </div>
                              </div>
                              <div className="field" style={{ marginBottom: 0 }}>
                                <label>When will they pay?</label>
                                <input value={payLater.promisedNote} onChange={e => setPayLater(pl => ({ ...pl, promisedNote: e.target.value }))} placeholder="e.g. Today by 6 PM, accounts team" />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
                          💼 Company Pay Later is available only for GST bills of a company with a 15-character GSTIN.
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Refund section — kept as-is */}
                  {refundDue > 0 && (
                    <>
                      <div className="pay-type-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
                        {PAYMENT_TYPES.map(pt => (
                          <button key={pt.value} className={`pay-type-btn ${refund.type === pt.value ? 'selected' : ''}`}
                            onClick={() => setRefund(f => ({ ...f, type: pt.value }))}
                            style={{ padding: '12px 4px', border: refund.type === pt.value ? `2px solid ${hotel.color}` : '1px solid var(--border)' }}>
                            <div style={{ fontSize: 18 }}>{pt.icon}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, marginTop: 4 }}>{pt.label}</div>
                          </button>
                        ))}
                      </div>
                      <div className="form-row-2">
                        <div className="field">
                          <label>Amount Refunded Now (₹)</label>
                          <input type="number" value={refund.amount} onChange={async e => {
                            const val = Number(e.target.value) || 0;
                            if (val > refundDue) { await notify(`Refund cannot exceed Refund Due: ${fmt(refundDue)}`); return; }
                            setRefund(f => ({ ...f, amount: e.target.value }));
                          }} style={{ fontSize: 16, fontWeight: 700 }} />
                        </div>
                        {['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(refund.type) && (
                          <div className="field">
                            <label>
                              {refund.type === 'cheque' ? 'Cheque Number (Last 5)' : refund.type === 'bank_transfer' ? 'Transaction ID (Last 5)' : 'Last 5 Digits (UPI/Card)'}
                              <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>
                            </label>
                            <input value={refund.last5} onChange={e => setRefund(f => ({ ...f, last5: e.target.value.slice(0, 5) }))} placeholder="Reference #" maxLength={5} />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {shiftActive === false && (
                    <div style={{ margin: '0 0 14px', padding: '12px 16px', background: 'rgba(231,76,60,0.1)', border: '1.5px solid var(--danger)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 20 }}>🔐</span>
                      <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--danger)' }}>No Active Shift!</div>
                        <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 2 }}>Please activate your shift before completing a checkout.</div>
                      </div>
                    </div>
                  )}
                  <button className="btn btn-gold" style={{ width: '100%', marginTop: 0, height: 50, fontSize: 16, justifyContent: 'center', background: shiftActive === false ? 'var(--muted)' : hotel.color, cursor: shiftActive === false ? 'not-allowed' : 'pointer' }}
                    onClick={async () => {
                      if (shiftActive === false) {
                        onSuccess?.('⚠️ Please activate your shift before checkout!', 'error');
                        return;
                      }
                      // Payments (+ company pay later) must add up to the bill exactly.
                      const settlementError = validateSettlement();
                      if (settlementError) {
                        onSuccess?.('⚠️ ' + settlementError, 'error');
                        return;
                      }
                      setLoading(true);
                      try {
                        const res = await invoicesApi.nextNumber(hotel.id, currentBillingType);
                        setInvNumPreview(res.invoiceNumber);
                        setShowPreview(true);
                      } catch (err) {
                        await notify(err.message || 'Error fetching invoice number');
                      }
                      setLoading(false);
                    }}
                    disabled={loading}
                  >
                    {shiftActive === false ? '🔒 Activate Shift to Checkout' : '🔍 PREVIEW INVOICE & PROCEED'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* BILL PREVIEW MODAL */}
      {showPreview && selected && (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: 980, padding: 0, overflow: 'hidden' }}>
            <div style={{ background: hotel.color, padding: '16px 28px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>📄 {previewDocumentLabel} Preview{checklist.billReviewed ? ` — #${previewInvNum}` : ''}</h3>
                {(previewUsesSeparateDamage || previewUsesSeparateExtras) && (
                  <div style={{ display: 'flex', background: 'rgba(0,0,0,0.15)', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)', padding: 3 }}>
                    <button onClick={() => setPreviewTab('stay')} style={{ border: 'none', borderRadius: 4, background: activePreviewTab === 'stay' ? '#fff' : 'transparent', color: activePreviewTab === 'stay' ? hotel?.color || '#1f2d3d' : 'rgba(255,255,255,0.9)', padding: '5px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s', boxShadow: activePreviewTab === 'stay' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none' }}>Stay Bill</button>
                    {previewUsesSeparateExtras && <button onClick={() => setPreviewTab('extras')} style={{ border: 'none', borderRadius: 4, background: activePreviewTab === 'extras' ? '#fff' : 'transparent', color: activePreviewTab === 'extras' ? hotel?.color || '#1f2d3d' : 'rgba(255,255,255,0.9)', padding: '5px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s', boxShadow: activePreviewTab === 'extras' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none' }}>Services Bill</button>}
                    {previewUsesSeparateDamage && <button onClick={() => setPreviewTab('damage')} style={{ border: 'none', borderRadius: 4, background: activePreviewTab === 'damage' ? '#fff' : 'transparent', color: activePreviewTab === 'damage' ? hotel?.color || '#1f2d3d' : 'rgba(255,255,255,0.9)', padding: '5px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s', boxShadow: activePreviewTab === 'damage' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none' }}>Damage Bill</button>}
                  </div>
                )}
              </div>
              <button className="btn btn-sm btn-ghost" onClick={() => { setShowPreview(false); setPreviewTab('stay'); }} style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>✕ Close</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 290px', gap: 0 }}>
              {/* PDF Mockup */}
              <div style={{ background: '#e6ebf2', padding: 28, maxHeight: '75vh', overflowY: 'auto' }}>
                <div style={{
                  background: 'white',
                  padding: '0 36px 34px',
                  boxShadow: '0 8px 28px rgba(31, 45, 61, 0.12)',
                  color: '#000',
                  fontFamily: '"Helvetica Neue", Arial, sans-serif',
                  fontSize: 12,
                  lineHeight: 1.6,
                  position: 'relative',
                  border: '1px solid #d8dfe8'
                }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: hotel.color }} />
                  {/* Subtle Logo Watermark */}
                  {(billDesign?.logoUrl || billDesign?.logo_url) && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '60%',
                      height: '60%',
                      backgroundImage: `url(${billDesign?.logoUrl || billDesign?.logo_url})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      opacity: 0.04,
                      pointerEvents: 'none',
                      zIndex: 0
                    }} />
                  )}
                  <div style={{ position: 'relative', zIndex: 1, paddingTop: 30 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 220px', gap: 20, alignItems: 'start', paddingBottom: 12, borderBottom: '1px solid #d8dfe8' }}>
                      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingTop: 2 }}>
                        {(billDesign?.logoUrl || billDesign?.logo_url) && (
                          <img src={billDesign?.logoUrl || billDesign?.logo_url} style={{ height: 58, width: 'auto', objectFit: 'contain', marginTop: 2 }} alt="Hotel Logo" />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 23, fontWeight: 900, color: '#1f2d3d', lineHeight: 1.1, marginTop: 0 }}>{previewHotelName}</div>
                          <div style={{ fontSize: 10, color: '#5d6774', marginTop: 5, fontWeight: 700 }}>
                            Phone: {previewHotelPhone}
                          </div>
                          <div style={{ fontSize: 9.6, color: '#667281', marginTop: 5, maxWidth: 390, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                            {previewHotelAddress}
                          </div>
                        </div>
                      </div>
                      <div style={{ border: '1px solid #d8dfe8', borderRadius: 12, overflow: 'hidden', background: '#fbfcfe', display: 'flex', flexDirection: 'column', alignSelf: 'start' }}>
                        <div style={{ background: hotel.color, color: '#fff', textAlign: 'center', padding: '7px 10px', fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          {previewDocumentLabel}
                        </div>
                        <div style={{ padding: '12px 16px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {checklist.billReviewed && (
                            <div style={{ fontSize: 16, fontWeight: 900, color: '#1f2d3d', lineHeight: 1.1 }}>#{previewInvNum}</div>
                          )}
                          <div style={{ fontSize: 10, color: '#5d6774', lineHeight: 1.55 }}>
                            <div><strong>Issued:</strong> {previewInvoiceDate}</div>
                            <div><strong>Time:</strong> {previewInvoiceTime}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'stretch' }}>
                      <div style={{ background: '#f7f9fc', border: '1px solid #dde3eb', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#1f2d3d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Guest Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', rowGap: 5, columnGap: 10, fontSize: 10.5 }}>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Name</div>
                          <div style={{ color: '#5d6774' }}>{previewGuestName}</div>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Address</div>
                          <div style={{ color: '#5d6774' }}>{previewGuestAddress}</div>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Phone</div>
                          <div style={{ color: '#5d6774' }}>{guest?.phone || 'NA'}</div>
                          {Boolean((previewCompanyName && previewCompanyName !== 'NA') || (previewCompanyGst && previewCompanyGst !== 'NA')) && (
                            <>
                              {previewCompanyName && previewCompanyName !== 'NA' && (
                                <>
                                  <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Company</div>
                                  <div style={{ color: '#5d6774' }}>{previewCompanyName}</div>
                                </>
                              )}
                              {previewCompanyGst && previewCompanyGst !== 'NA' && (
                                <>
                                  <div style={{ color: '#1f2d3d', fontWeight: 700 }}>GST No.</div>
                                  <div style={{ color: '#5d6774' }}>{previewCompanyGst}</div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div style={{ background: '#f7f9fc', border: '1px solid #dde3eb', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#1f2d3d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Stay Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '82px 1fr', rowGap: 5, columnGap: 10, fontSize: 10.5 }}>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Room</div>
                          <div style={{ color: '#5d6774' }}>{selectedRooms.join(', ')}</div>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Category</div>
                          <div style={{ color: '#5d6774' }}>{activeRoomTypes || selected.roomType || selected.room_type || 'NA'}</div>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Check-in</div>
                          <div style={{ color: '#5d6774' }}>{fmtDate(selected.checkIn || selected.check_in)} {(selected.checkInTime || selected.check_in_time || '').trim()}</div>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Check-out</div>
                          <div style={{ color: '#5d6774' }}>{fmtDate(selected.checkOut || selected.check_out)}</div>
                          <div style={{ color: '#1f2d3d', fontWeight: 700 }}>Duration</div>
                          <div style={{ color: '#5d6774' }}>{selected.nights} {stayUnitLabel}</div>
                        </div>
                      </div>
                    </div>
                    {previewUsesSeparateDamage && (
                      <div style={{ marginTop: 14, padding: '10px 12px', background: '#fff7e8', border: '1px solid #f1d08d', borderRadius: 10, fontSize: 10.5, color: '#7a5a17', fontWeight: 700 }}>
                        Separate damage bill will also be created for {fmt(Number(damageCharges) || 0)} after final checkout.
                      </div>
                    )}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 18, border: '1px solid #dde3eb' }}>
                      <thead>
                        <tr style={{ background: '#243447', color: '#fff' }}>
                          <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 10.5, fontWeight: 800 }}>Description</th>
                          <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: 10.5, fontWeight: 800 }}>SAC Code</th>
                          <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: 10.5, fontWeight: 800 }}>Details</th>
                          <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 10.5, fontWeight: 800 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewInvoiceItems.map((item, index) => (
                          <tr key={`${item.description}-${index}`} style={{
                            background: item.tone === 'danger'
                              ? '#fff5f5'
                              : item.tone === 'success'
                                ? '#f4fcf7'
                                : index % 2 === 1 ? '#f7f9fc' : '#fff',
                            borderBottom: '1px solid #e1e7ee'
                          }}>
                            <td style={{ padding: '11px 12px', fontSize: 10.8, fontWeight: 700, color: item.tone === 'danger' ? '#ac3939' : item.tone === 'success' ? '#217c51' : '#1f2d3d' }}>{item.description}</td>
                            <td style={{ textAlign: 'center', padding: '11px 12px', fontSize: 10, color: '#667281' }}>{item.code || '-'}</td>
                            <td style={{ textAlign: 'center', padding: '11px 12px', fontSize: 10, color: '#667281' }}>{item.details || '-'}</td>
                            <td style={{ textAlign: 'right', padding: '11px 12px', fontSize: 11, fontWeight: 800, color: item.tone === 'danger' ? '#ac3939' : item.tone === 'success' ? '#217c51' : '#1f2d3d' }}>{formatCurrencyCell(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#2c3e50', color: '#fff' }}>
                          <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 10.5, fontWeight: 800 }}>GRAND TOTAL</th>
                          <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: 10.5, fontWeight: 800 }}>-</th>
                          <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: 10.5, fontWeight: 800 }}>-</th>
                          <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, fontWeight: 900 }}>{fmt(previewDisplayTotal)}</th>
                        </tr>
                      </tfoot>
                    </table>

                    <div style={{ marginTop: 10, fontSize: 10, fontStyle: 'italic', color: '#667281' }}>
                      Amount in words: {numToWords(previewDisplayTotal)}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.9fr', gap: 14, marginTop: 18 }}>
                      <div style={{ background: '#f7f9fc', border: '1px solid #dde3eb', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#1f2d3d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Settlement Details</div>
                        {previewSettlementEntries.length > 0 ? previewSettlementEntries.map((entry, index) => (
                          <div key={`${entry.label}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 10.4, color: '#5d6774', marginBottom: 6 }}>
                            <span>{entry.label}</span>
                            <span style={{ fontWeight: 700, color: '#1f2d3d' }}>{fmt(entry.amount)}</span>
                          </div>
                        )) : (
                          <div style={{ fontSize: 10.4, color: '#667281' }}>No payment entries recorded.</div>
                        )}
                      </div>

                      <div style={{ background: '#f7f9fc', border: '1px solid #dde3eb', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#1f2d3d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Invoice Summary</div>
                        {[
                          ['Taxable Amount', fmt(previewTaxableBase)],
                          ['GST Included', fmt(previewBillData?.gstAmount || 0)],
                          ['Total Paid', fmt(previewTotalPaid)],
                        ].map(([label, value], index) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: index === 2 ? 11 : 10.4, color: index === 2 ? '#1f2d3d' : '#5d6774', fontWeight: index === 2 ? 800 : 500, marginBottom: 7 }}>
                            <span>{label}</span>
                            <span style={{ fontWeight: index === 2 ? 900 : 700, color: '#1f2d3d' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginTop: 18, padding: '14px 16px', background: '#f7f9fc', borderRadius: 12, border: '1px solid #dde3eb' }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#1f2d3d', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Terms & Conditions</div>
                      <div style={{ fontSize: 10.4, whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#5d6774' }}>
                        {previewTermsText}
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, color: '#667281' }}>
                      <div style={{ width: 160, textAlign: 'center', height: 56, display: 'flex', alignItems: 'flex-end' }}>
                        <div style={{ borderTop: '1px solid #cfd7e2', paddingTop: 6, fontSize: 10, width: '100%' }}>Guest Signature</div>
                      </div>
                      <div style={{ width: 190, textAlign: 'center', height: 56, display: 'flex', alignItems: 'flex-end' }}>
                        <div style={{ borderTop: '1px solid #cfd7e2', paddingTop: 6, fontSize: 10, width: '100%' }}>
                          {previewSignatureLabel}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 26, paddingTop: 10, borderTop: '1px solid #d8dfe8', textAlign: 'center', color: '#8a96a6', fontSize: 9.5 }}>
                      {previewFooterText}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right confirm panel */}
              <div style={{ padding: 22, borderLeft: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '75vh', overflowY: 'auto' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>Confirm Final Receipt</div>

                <div style={{ background: 'var(--s1)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>Quick Bill Corrections</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>Review the full stay details here. Guest and company corrections still update the bill preview instantly.</div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setShowGuestEdit(prev => !prev)}
                      style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      {showGuestEdit ? 'Hide' : 'Edit Details'}
                    </button>
                  </div>

                  <div style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: 8
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                      Stay Snapshot
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', fontSize: 11 }}>
                      <div>
                        <div style={{ color: 'var(--muted)' }}>Guest</div>
                        <div style={{ fontWeight: 700, color: 'var(--text)' }}>{formatDisplayName(guest?.salutation, getTextValue(guest?.name))}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--muted)' }}>Advance / Previous Payments</div>
                        <div style={{ fontWeight: 700, color: 'var(--text)' }}>{fmt(paidSoFarTotal)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--muted)' }}>Room Number</div>
                        <div style={{ fontWeight: 700, color: 'var(--text)' }}>{selectedRooms.join(', ') || selected?.room || 'NA'}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--muted)' }}>Room Type</div>
                        <div style={{ fontWeight: 700, color: 'var(--text)' }}>{activeRoomTypes || selected?.roomType || selected?.room_type || 'NA'}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--muted)' }}>Check-in</div>
                        <div style={{ fontWeight: 700, color: 'var(--text)' }}>
                          {fmtDate(selected?.checkIn || selected?.check_in)} {(selected?.checkInTime || selected?.check_in_time || '').trim()}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--muted)' }}>Check-out</div>
                        <div style={{ fontWeight: 700, color: 'var(--text)' }}>{fmtDate(selected?.checkOut || selected?.check_out)}</div>
                      </div>
                    </div>
                  </div>

                  {showGuestEdit && (
                    <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 10 }}>Salutation</label>
                        <select
                          value={guest?.salutation || ''}
                          onChange={e => handleGuestFieldChange('salutation', e.target.value)}
                          style={{ fontSize: 13 }}
                        >
                          <option value="">None</option>
                          <option value="Mr.">Mr.</option>
                          <option value="Mrs.">Mrs.</option>
                          <option value="Ms.">Ms.</option>
                          <option value="Dr.">Dr.</option>
                        </select>
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 10 }}>Guest Name</label>
                        <input
                          value={getTextValue(guest?.name) || ''}
                          onChange={e => handleGuestFieldChange('name', e.target.value)}
                          style={{ fontSize: 13 }}
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 10 }}>Phone Number</label>
                        <input
                          value={guest?.phone || ''}
                          onChange={e => handleGuestFieldChange('phone', e.target.value)}
                          style={{ fontSize: 13 }}
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 10 }}>Address</label>
                        <textarea
                          value={getTextValue(guest?.address) || ''}
                          onChange={e => handleGuestFieldChange('address', e.target.value)}
                          rows={3}
                          style={{ fontSize: 13, resize: 'vertical' }}
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)' }}>Billing Type (Tax / Non-Tax)</label>
                        <select
                          value={currentBillingType}
                          onChange={e => setBillingOverride(e.target.value)}
                          style={{ fontSize: 13, fontWeight: 700 }}
                        >
                          <option value="non-gst">📄 Non-GST Bill (Regular Standard Bill)</option>
                          <option value="gst">🧾 GST Tax Bill (5% Tax Invoice)</option>
                        </select>
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#2c3e50' }}>🏢 Company Name (For GST Invoice)</label>
                        <input
                          placeholder="e.g. Acme Technologies Pvt Ltd"
                          value={getTextValue(selected?.company) || getTextValue(guest?.company) || ''}
                          onChange={e => handleGuestFieldChange('company', e.target.value, true)}
                          style={{ fontSize: 13 }}
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#e8b86d' }}>🆔 Company GSTIN / GST Number (15 Characters)</label>
                        <input
                          placeholder="e.g. 37AAAAA0000A1Z5 (15 Characters)"
                          maxLength={15}
                          value={getTextValue(selected?.companyGst) || getTextValue(guest?.companyGst) || ''}
                          onChange={e => handleGuestFieldChange('companyGst', e.target.value, true)}
                          style={{ fontSize: 13, letterSpacing: '0.5px', fontFamily: 'monospace' }}
                        />
                        {Boolean(getTextValue(selected?.companyGst) || getTextValue(guest?.companyGst)) && (
                          <div style={{
                            fontSize: 10,
                            marginTop: 2,
                            fontWeight: 700,
                            color: (getTextValue(selected?.companyGst) || getTextValue(guest?.companyGst)).length === 15 ? '#27ae60' : '#e74c3c'
                          }}>
                            {(getTextValue(selected?.companyGst) || getTextValue(guest?.companyGst)).length === 15
                              ? '✓ Valid 15-character GSTIN format'
                              : `Length: ${(getTextValue(selected?.companyGst) || getTextValue(guest?.companyGst)).length}/15 characters`}
                          </div>
                        )}
                      </div>

                      <div style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.5 }}>
                        Any correction here will reset <strong>Bill Reviewed by Customer</strong> so the updated bill can be checked again before final print.
                      </div>

                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={handleSaveGuestDetails}
                        disabled={savingGuestDetails}
                        style={{
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          border: '1px solid var(--accent)',
                          color: 'var(--accent)'
                        }}
                      >
                        {savingGuestDetails ? 'Saving Details...' : 'Save Guest Details'}
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ background: 'var(--s1)', padding: 10, borderRadius: 8, display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', border: '1px solid var(--border)' }} onClick={async () => {
                  const newType = currentBillingType === 'gst' ? 'non-gst' : 'gst';
                  setBillingOverride(newType);
                  // Refresh invoice number for the switched billing type
                  try {
                    const res = await invoicesApi.nextNumber(hotel.id, newType);
                    setInvNumPreview(res.invoiceNumber);
                  } catch (e) { console.error('Failed to refresh inv number', e); }
                }}>
                  <div style={{ width: 34, height: 18, background: currentBillingType === 'gst' ? 'var(--teal)' : '#ccc', borderRadius: 9, position: 'relative', transition: '0.3s' }}>
                    <div style={{ width: 14, height: 14, background: 'white', borderRadius: '50%', position: 'absolute', top: 2, left: currentBillingType === 'gst' ? 18 : 2, transition: '0.3s' }} />
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700 }}>{currentBillingType === 'gst' ? 'GST INVOICE' : 'INVOICE'}</div>
                </div>

                <div style={{ background: 'var(--accent)', padding: '16px 20px', borderRadius: 12, color: 'white' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12, opacity: 0.9 }}>📋 Mandatory Checkout Checklist</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { id: 'keys', label: 'Room Keys Handled ' },
                      { id: 'damage', label: 'Room Inspected / No Damage ' },
                      { id: 'payment', label: 'Full Payment Settlement Done ' },
                      { id: 'billReviewed', label: 'Bill Reviewed by Customer ' },
                      { id: 'others', label: 'Other Verification Complete ' },
                    ].map(item => (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setChecklist(prev => ({ ...prev, [item.id]: !prev[item.id] }))}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, background: checklist[item.id] ? 'var(--success)' : 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.4)', transition: '0.2s' }}>
                          {checklist[item.id] && <span style={{ fontSize: 12 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>Total Amount</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: hotel.color }}>{fmt(previewDisplayTotal)}</div>
                </div>

                <div style={{ background: 'var(--s1)', padding: 13, borderRadius: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                    <span>Previous Payments</span>
                    <span style={{ fontWeight: 700 }}>{fmt(paidSoFar)}</span>
                  </div>
                  {refundDue > 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--success)', fontSize: 14 }}>
                      <span>Refund via {formatPaymentTypeLabel(refund.type)}</span>
                      <span style={{ fontWeight: 800 }}>{fmt(refund.amount)}</span>
                    </div>
                  ) : settlementRecord.map((entry, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: entry.isDue ? 'var(--gold)' : 'var(--danger)', fontSize: 13, marginTop: 4 }}>
                      <span>{entry.isDue ? payLaterLabel : `Now via ${formatPaymentTypeLabel(entry.type)}`}</span>
                      <span style={{ fontWeight: 800 }}>{fmt(entry.amount)}</span>
                    </div>
                  ))}
                </div>

                {/* Action buttons */}
                <button className="btn btn-gold" style={{ width: '100%', height: 52, fontSize: 15, background: 'var(--success)', border: 'none', justifyContent: 'center', opacity: Object.values(checklist).every(v => v) ? 1 : 0.4, cursor: Object.values(checklist).every(v => v) ? 'pointer' : 'not-allowed' }}
                  onClick={async () => {
                    if (!Object.values(checklist).every(v => v)) {
                      await notify('Please complete the checkout checklist first!');
                      return;
                    }
                    const settlementError = validateSettlement();
                    if (settlementError) { await notify(settlementError); return; }
                    handleCheckout();
                    setShowPreview(false);
                    setChecklist({ keys: false, damage: false, payment: false, billReviewed: false, others: false });
                  }} disabled={loading}>
                  {loading ? '⌛ Processing...' : '🖨️ Confirm & Print Bill'}
                </button>

                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 10 }}>Share Digital Copy</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', gap: 8, border: '1px solid var(--accent)', color: 'var(--accent)', fontWeight: 700 }}
                      onClick={handlePrintPreview}>
                      🖨️ Print Preview Bill
                    </button>
                    <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', gap: 8, border: '1px solid #25D366', color: '#25D366', fontWeight: 700 }}
                      onClick={handleWhatsAppShare}>
                      💬 Share via WhatsApp
                    </button>
                    <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', gap: 8, border: '1px solid #4a90e2', color: '#4a90e2', fontWeight: 700 }}
                      onClick={handleEmailShare}>
                      ✉️ Send via Email
                    </button>
                    <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', gap: 8, fontWeight: 700 }}
                      onClick={handleSavePDF}>
                      💾 Save as PDF
                    </button>
                  </div>
                </div>

                <p style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'center', marginTop: 4 }}>
                  Confirming will mark room as cleaning and save PDF to guest history.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
